import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, RefreshCw, Users, FileText, Settings, MessageCircle, Gift, Bell, X, Copy, Send, Trash2, Lock, Activity, Eye, Shield, Search, Gamepad2, CheckCircle2, ShieldCheck, BarChart3, UserCheck, UserX, TrendingUp } from "lucide-react";
import { COUNTRIES } from "../config/countries";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { API } from "../config/constants";
import P2PAdminPanel from "../components/P2PAdminPanel";

const AdminPage = () => {
  const { user, logout, authAxios } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // Create axios instance with JWT token (not static admin_token)
  const adminApi = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        'Authorization': `Bearer ${user?.access_token || ''}`
      }
    });
    return instance;
  }, [user?.access_token]);
  
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [pendingTx, setPendingTx] = useState([]);
  const [adminPin, setAdminPin] = useState("");
  const [oldPin, setOldPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmNewPin, setConfirmNewPin] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ phone: "", name: "", pin: "" });
  const [bulkEmailSubject, setBulkEmailSubject] = useState("");
  const [bulkEmailMessage, setBulkEmailMessage] = useState("");
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);
  const [sortBy, setSortBy] = useState("name"); // name (alphabetical), stake or newest
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [usersPerPage] = useState(100);
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  // Live Users states
  const [liveUsers, setLiveUsers] = useState({ count: 0, users: [] });
  const [showLiveUsers, setShowLiveUsers] = useState(false);
  // Announcement states
  const [announcementTitle, setAnnouncementTitle] = useState("");
  const [announcementMessage, setAnnouncementMessage] = useState("");
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [activeAnnouncement, setActiveAnnouncement] = useState(null);
  // Promo Code states
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", bonus_type: "ltg", bonus_amount: "", max_uses: "100", expires_at: "" });
  // Notification states
  const [notifForm, setNotifForm] = useState({ user_id: "", title: "", message: "", notification_type: "info" });
  const [sendingNotif, setSendingNotif] = useState(false);
  // Email Template states
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("welcome");
  const [templateForm, setTemplateForm] = useState({ subject: "", body: "" });
  const [savingTemplate, setSavingTemplate] = useState(false);
  // Referral Bonus Settings
  const [referralBonusPercent, setReferralBonusPercent] = useState(10);
  const [pendingBonusCount, setPendingBonusCount] = useState(0);
  const [pendingBonusList, setPendingBonusList] = useState([]);
  const [showPendingList, setShowPendingList] = useState(false);
  const [savingReferralBonus, setSavingReferralBonus] = useState(false);
  // Welcome Email Setting
  const [welcomeEmailEnabled, setWelcomeEmailEnabled] = useState(true);
  const [togglingWelcomeEmail, setTogglingWelcomeEmail] = useState(false);
  // Support Chat states
  const [supportChats, setSupportChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [adminReply, setAdminReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  // Balance Adjustment
  const [balanceUser, setBalanceUser] = useState(null);
  const [balanceForm, setBalanceForm] = useState({ balance_type: "usdt_balance", amount: "", reason: "" });
  const [adjustingBalance, setAdjustingBalance] = useState(false);
  // Admin Unstake
  const [unstakeUser, setUnstakeUser] = useState(null);
  const [unstakeForm, setUnstakeForm] = useState({ ltg_amount: "", reason: "" });
  const [unstaking, setUnstaking] = useState(false);
  // Login Logs
  const [logsUser, setLogsUser] = useState(null);
  const [loginLogs, setLoginLogs] = useState([]);
  // Bulk Selection for users
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  // Blocked Names
  const [blockedNames, setBlockedNames] = useState([]);
  const [newBlockedName, setNewBlockedName] = useState("");
  const [addingBlockedName, setAddingBlockedName] = useState(false);
  // Referrals View
  const [viewingReferrals, setViewingReferrals] = useState(null);
  const [referralsList, setReferralsList] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  // Login as User View
  const [viewingAsUser, setViewingAsUser] = useState(null);
  const [userViewData, setUserViewData] = useState(null);
  const [loadingUserView, setLoadingUserView] = useState(false);
  // Financial Report
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [financialReport, setFinancialReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  // Deposit Wallet Address
  const [depositWalletAddress, setDepositWalletAddress] = useState("");
  // Per-country activation fees state
  const [activationFees, setActivationFees] = useState({ default_fee_usd: 50, per_country: {} });
  const [afCountry, setAfCountry] = useState("");
  const [afFee, setAfFee] = useState("");
  const [afNote, setAfNote] = useState("");
  const [afSaving, setAfSaving] = useState(false);
  // Bulk-deactivate-by-country state
  const [bdCountry, setBdCountry] = useState("");
  const [bdReason, setBdReason] = useState("");
  const [bdPreview, setBdPreview] = useState(null); // {matched: N}
  const [bdBusy, setBdBusy] = useState(false);
  const [newDepositWallet, setNewDepositWallet] = useState("");
  const [savingDepositWallet, setSavingDepositWallet] = useState(false);
  // Suspicious IPs States
  const [suspiciousIPs, setSuspiciousIPs] = useState(null);
  const [loadingSuspiciousIPs, setLoadingSuspiciousIPs] = useState(false);
  const [expandedIP, setExpandedIP] = useState(null);
  const [ipSearchQuery, setIpSearchQuery] = useState("");
  const [ipSearchResult, setIpSearchResult] = useState(null);
  const [searchingIP, setSearchingIP] = useState(false);
  // Suspicious Logins States
  const [suspiciousLogins, setSuspiciousLogins] = useState(null);
  const [loadingSuspiciousLogins, setLoadingSuspiciousLogins] = useState(false);
  const [ipLocationModal, setIpLocationModal] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  // Investigation Search States
  const [investigateQuery, setInvestigateQuery] = useState("");
  const [investigateResults, setInvestigateResults] = useState(null);
  const [investigating, setInvestigating] = useState(false);

  // Debounced search to avoid too many API calls
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Track which tabs have been loaded to avoid re-fetching
  const [loadedTabs, setLoadedTabs] = useState({ users: false, transactions: false, support: false, settings: false, games: false, analytics: false });
  const [loadingTab, setLoadingTab] = useState("");

  // Games admin state
  const [gameStats, setGameStats] = useState(null);
  const [pendingGameDeposits, setPendingGameDeposits] = useState([]);
  const [pendingGameWithdrawals, setPendingGameWithdrawals] = useState([]);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [todayLoginsList, setTodayLoginsList] = useState([]);
  const [oneTimeUsersList, setOneTimeUsersList] = useState([]);
  const [analyticsView, setAnalyticsView] = useState("overview"); // overview | today | onetime

  useEffect(() => {
    if (!user?.is_admin) { navigate("/"); return; }
    // Only load users initially (most common tab)
    fetchUsers();
    fetchLiveUsers();
    // Refresh live users every 30 seconds
    const liveInterval = setInterval(fetchLiveUsers, 30000);
    return () => clearInterval(liveInterval);
  }, [user]);

  // Load tab-specific data when tab changes (lazy loading)
  useEffect(() => {
    if (tab === "users" && (sortBy || currentPage || debouncedSearch)) {
      fetchUsers();
    } else if (tab === "transactions" && !loadedTabs.transactions) {
      fetchTransactions();
    } else if (tab === "support" && !loadedTabs.support) {
      fetchSupportChats();
    } else if (tab === "settings" && !loadedTabs.settings) {
      fetchSettings();
    } else if (tab === "games" && !loadedTabs.games) {
      fetchGamesAdmin();
    } else if (tab === "analytics" && !loadedTabs.analytics) {
      fetchAnalytics();
    }
  }, [tab, sortBy, currentPage, debouncedSearch]);

  const fetchAnalytics = async () => {
    try {
      setLoadingTab("analytics");
      const [overviewRes, todayRes, oneTimeRes] = await Promise.all([
        adminApi.get(`/admin/analytics/user-activity`),
        adminApi.get(`/admin/analytics/today-logins?limit=200`),
        adminApi.get(`/admin/analytics/one-time-users?limit=200`),
      ]);
      setAnalytics(overviewRes.data);
      setTodayLoginsList(todayRes.data.users || []);
      setOneTimeUsersList(oneTimeRes.data.users || []);
      setLoadedTabs(prev => ({ ...prev, analytics: true }));
    } catch (e) {
      console.error(e);
      toast.error("Failed to load analytics");
    } finally {
      setLoadingTab("");
    }
  };

  const fetchGamesAdmin = async () => {
    try {
      setLoadingTab("games");
      const [statsRes, depRes, wdRes] = await Promise.all([
        adminApi.get(`/admin/games/stats`),
        adminApi.get(`/admin/games/pending-deposits`),
        adminApi.get(`/admin/games/pending-withdrawals`)
      ]);
      setGameStats(statsRes.data);
      setPendingGameDeposits(depRes.data.deposits || []);
      setPendingGameWithdrawals(wdRes.data.withdrawals || []);
      setLoadedTabs(prev => ({ ...prev, games: true }));
    } catch (e) {
      console.error(e);
      toast.error("Failed to load games data");
    } finally {
      setLoadingTab("");
    }
  };

  const approveGameDeposit = async (txId) => {
    try {
      await adminApi.post(`/admin/games/approve-deposit/${txId}`);
      toast.success("Deposit approved");
      setLoadedTabs(prev => ({ ...prev, games: false }));
      fetchGamesAdmin();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve");
    }
  };

  const rejectGameDeposit = async (txId) => {
    if (!window.confirm("Reject this deposit?")) return;
    try {
      await adminApi.post(`/admin/games/reject-deposit/${txId}`);
      toast.success("Deposit rejected");
      setLoadedTabs(prev => ({ ...prev, games: false }));
      fetchGamesAdmin();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject");
    }
  };

  const approveGameWithdrawal = async (txId) => {
    try {
      await adminApi.post(`/admin/games/approve-withdrawal/${txId}`);
      toast.success("Withdrawal approved");
      setLoadedTabs(prev => ({ ...prev, games: false }));
      fetchGamesAdmin();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve");
    }
  };

  const rejectGameWithdrawal = async (txId) => {
    if (!window.confirm("Reject this withdrawal? Amount will be refunded to user.")) return;
    try {
      await adminApi.post(`/admin/games/reject-withdrawal/${txId}`);
      toast.success("Withdrawal rejected & refunded");
      setLoadedTabs(prev => ({ ...prev, games: false }));
      fetchGamesAdmin();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject");
    }
  };

  const fetchLiveUsers = async () => {
    try {
      const res = await adminApi.get(`/admin/live-users`);
      setLiveUsers(res.data);
    } catch (e) { console.error(e); }
  };

  // Fetch only users (for Users tab)
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const usersRes = await adminApi.get(`/admin/users?sort_by=${sortBy}&page=${currentPage}&limit=${usersPerPage}&search=${encodeURIComponent(debouncedSearch)}`);
      setUsers(usersRes.data.users);
      setTotalUsers(usersRes.data.total_users);
      setTotalPages(usersRes.data.total_pages);
      setLoadedTabs(prev => ({ ...prev, users: true }));
    } catch (e) { 
      console.error(e); 
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch transactions (for Transactions tab)
  const fetchTransactions = async () => {
    try {
      setLoadingTab("transactions");
      const res = await adminApi.get(`/admin/pending-transactions`);
      setPendingTx(res.data);
      setLoadedTabs(prev => ({ ...prev, transactions: true }));
    } catch (e) { 
      console.error(e); 
    } finally {
      setLoadingTab("");
    }
  };

  // Fetch support chats (for Support tab)
  const fetchSupportChats = async () => {
    try {
      setLoadingTab("support");
      const res = await adminApi.get(`/admin/support/chats`);
      setSupportChats(res.data || []);
      setLoadedTabs(prev => ({ ...prev, support: true }));
    } catch (e) { 
      console.error(e); 
    } finally {
      setLoadingTab("");
    }
  };

  const saveActivationFee = async () => {
    const c = (afCountry || "").trim();
    const fee = parseFloat(afFee);
    if (!c) { toast.error("Enter country name"); return; }
    if (isNaN(fee) || fee < 0) { toast.error("Enter a valid fee (>= 0)"); return; }
    setAfSaving(true);
    try {
      await adminApi.post(`/admin/activation-fees`, { country: c, fee_usd: fee, note: afNote });
      toast.success(`${c.toLowerCase()} → $${fee} saved`);
      const feesRes = await adminApi.get(`/admin/activation-fees`);
      setActivationFees(feesRes.data);
      setAfCountry(""); setAfFee(""); setAfNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setAfSaving(false);
  };

  const deleteActivationFee = async (country) => {
    if (!window.confirm(`Reset ${country} to default $${activationFees.default_fee_usd}?`)) return;
    try {
      await adminApi.delete(`/admin/activation-fees/${country}`);
      toast.success(`${country} reset to default`);
      const feesRes = await adminApi.get(`/admin/activation-fees`);
      setActivationFees(feesRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const previewBulkDeactivate = async () => {
    if (!bdCountry) { toast.error("Select country"); return; }
    setBdBusy(true);
    try {
      const r = await adminApi.post(`/admin/activation-fees/bulk-deactivate`, {
        country: bdCountry, reason: bdReason, dry_run: true,
      });
      setBdPreview({ matched: r.data.would_affect });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
    setBdBusy(false);
  };

  const confirmBulkDeactivate = async () => {
    if (!bdPreview) return;
    const msg = `Really deactivate ${bdPreview.matched} ${bdCountry} accounts? They will have to deposit again to reactivate.`;
    if (!window.confirm(msg)) return;
    setBdBusy(true);
    try {
      const r = await adminApi.post(`/admin/activation-fees/bulk-deactivate`, {
        country: bdCountry, reason: bdReason, dry_run: false,
      });
      toast.success(`✅ ${r.data.deactivated} ${bdCountry} accounts deactivated`);
      setBdPreview(null); setBdCountry(""); setBdReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBdBusy(false);
  };

  // Fetch settings data (for Settings tab) - loads multiple APIs
  const fetchSettings = async () => {
    try {
      setLoadingTab("settings");
      // Single optimized API call instead of 7 separate calls
      const res = await adminApi.get(`/admin/settings-all`);
      const data = res.data;
      
      setAdminPin(data.pin);
      setPromoCodes(data.promo_codes || []);
      setEmailTemplates(data.email_templates || []);
      setActiveAnnouncement(data.active_announcement);
      setReferralBonusPercent(data.referral_bonus_percent);
      setPendingBonusCount(data.pending_bonuses);
      setPendingBonusList(data.pending_list || []);
      setWelcomeEmailEnabled(data.welcome_email_enabled);
      setBlockedNames(data.blocked_names || []);
      setDepositWalletAddress(data.deposit_wallet_address || "");
      setNewDepositWallet(data.deposit_wallet_address || "");
      // Load per-country activation fees
      try {
        const feesRes = await adminApi.get(`/admin/activation-fees`);
        setActivationFees(feesRes.data || { default_fee_usd: 50, per_country: {} });
      } catch (_) { /* silent */ }
      setLoadedTabs(prev => ({ ...prev, settings: true }));
    } catch (e) { 
      console.error(e); 
    } finally {
      setLoadingTab("");
    }
  };

  // Blocked Names Functions
  const addBlockedName = async () => {
    if (!newBlockedName.trim()) { toast.error("नाम डालें"); return; }
    setAddingBlockedName(true);
    try {
      await adminApi.post(`/admin/blocked-names`, { name: newBlockedName.trim() });
      toast.success(`✅ "${newBlockedName}" blocked`);
      setNewBlockedName("");
      const res = await adminApi.get(`/admin/blocked-names`);
      setBlockedNames(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to block name");
    }
    setAddingBlockedName(false);
  };

  const removeBlockedName = async (name) => {
    if (!window.confirm(`"${name}" को unblock करना है?`)) return;
    try {
      await adminApi.delete(`/admin/blocked-names/${encodeURIComponent(name)}`);
      toast.success(`✅ "${name}" unblocked`);
      setBlockedNames(prev => prev.filter(n => n.name_lower !== name.toLowerCase()));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to unblock");
    }
  };

  // Refresh data for current tab
  const refreshCurrentTab = () => {
    if (tab === "users") fetchUsers();
    else if (tab === "transactions") { setLoadedTabs(prev => ({ ...prev, transactions: false })); fetchTransactions(); }
    else if (tab === "support") { setLoadedTabs(prev => ({ ...prev, support: false })); fetchSupportChats(); }
    else if (tab === "settings") { setLoadedTabs(prev => ({ ...prev, settings: false })); fetchSettings(); }
    else if (tab === "games") { setLoadedTabs(prev => ({ ...prev, games: false })); fetchGamesAdmin(); }
  };

  // Legacy fetchData for backward compatibility (used by some functions)
  const fetchData = () => refreshCurrentTab();

  // Toggle Welcome Email
  const toggleWelcomeEmail = async () => {
    setTogglingWelcomeEmail(true);
    try {
      const newValue = !welcomeEmailEnabled;
      await adminApi.post(`/admin/settings/welcome-email?enabled=${newValue}`);
      setWelcomeEmailEnabled(newValue);
      toast.success(`Welcome Email ${newValue ? 'ON' : 'OFF'} किया गया`);
    } catch (e) {
      toast.error("Failed to update setting");
    }
    setTogglingWelcomeEmail(false);
  };

  // Update Deposit Wallet Address
  const updateDepositWallet = async () => {
    if (!newDepositWallet.trim() || newDepositWallet.length < 10) {
      toast.error("Valid wallet address दें");
      return;
    }
    setSavingDepositWallet(true);
    try {
      await adminApi.post(`/admin/deposit-wallet`, { address: newDepositWallet.trim() });
      setDepositWalletAddress(newDepositWallet.trim());
      toast.success("Deposit wallet address update हो गया!");
    } catch (e) {
      toast.error("Failed to update wallet address");
    }
    setSavingDepositWallet(false);
  };

  // Suspicious IPs Functions
  const fetchSuspiciousIPs = async () => {
    setLoadingSuspiciousIPs(true);
    try {
      const res = await adminApi.get(`/admin/duplicate-ips`);
      // Sort by number of accounts (highest first)
      const sortedDuplicates = Object.entries(res.data.duplicates || {})
        .sort((a, b) => b[1].length - a[1].length)
        .reduce((acc, [ip, users]) => {
          acc[ip] = users;
          return acc;
        }, {});
      setSuspiciousIPs({
        ...res.data,
        duplicates: sortedDuplicates
      });
    } catch (e) {
      toast.error("Failed to fetch suspicious IPs");
    }
    setLoadingSuspiciousIPs(false);
  };

  const blockIPUsers = async (ip, userIds) => {
    if (!window.confirm(`क्या आप इस IP (${ip}) के ${userIds.length} accounts को block करना चाहते हैं?`)) return;
    try {
      await adminApi.post(`/admin/bulk-block-users`, {
        user_ids: userIds,
        reason: `Suspicious IP: ${ip} - Multiple accounts`
      });
      toast.success(`✅ ${userIds.length} users blocked from IP ${ip}`);
      fetchSuspiciousIPs(); // Refresh
    } catch (e) {
      toast.error("Failed to block users");
    }
  };

  // Search user by phone/email and find their IP
  const searchUserIP = async () => {
    if (!ipSearchQuery.trim()) {
      toast.error("Phone या Email enter करें");
      return;
    }
    setSearchingIP(true);
    setIpSearchResult(null);
    try {
      // First find the user
      const userRes = await adminApi.get(`/admin/users?search=${encodeURIComponent(ipSearchQuery)}&limit=1`);
      if (!userRes.data.users || userRes.data.users.length === 0) {
        toast.error("User नहीं मिला");
        setSearchingIP(false);
        return;
      }
      
      const foundUser = userRes.data.users[0];
      
      // Get IP details (registration or login logs) with location
      const ipDetailsRes = await adminApi.get(`/admin/user-ip-details/${foundUser.id}`);
      const ipData = ipDetailsRes.data;
      
      setIpSearchResult({
        user: foundUser,
        registration_ip: ipData.registration_ip,
        recent_login_ip: ipData.recent_login_ip,
        recent_login_time: ipData.recent_login_time,
        location: ipData.location,
        otherUsers: ipData.same_ip_users || [],
        totalOnIP: ipData.same_ip_count || 1,
        login_history: ipData.login_history || [],
        message: ipData.message
      });
    } catch (e) {
      toast.error("Search failed");
    }
    setSearchingIP(false);
  };

  // Get IP location on click
  const fetchIPLocation = async (ip) => {
    try {
      const res = await adminApi.get(`/admin/ip-location/${ip}`);
      return res.data;
    } catch (e) {
      toast.error("Failed to fetch location");
      return null;
    }
  };

  // Suspicious Logins Functions
  const fetchSuspiciousLogins = async () => {
    setLoadingSuspiciousLogins(true);
    try {
      const res = await adminApi.get(`/admin/suspicious-logins`);
      setSuspiciousLogins(res.data);
    } catch (e) {
      toast.error("Failed to fetch suspicious logins");
    }
    setLoadingSuspiciousLogins(false);
  };

  const blockIP = async (ip, reason = "Suspicious login activity") => {
    if (!window.confirm(`क्या आप IP ${ip} को block करना चाहते हैं?`)) return;
    try {
      await adminApi.post(`/admin/block-ip`, { ip, reason });
      toast.success(`✅ IP ${ip} blocked`);
      fetchSuspiciousLogins();
      fetchSuspiciousIPs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to block IP");
    }
  };

  const unblockIP = async (ip) => {
    if (!window.confirm(`क्या आप IP ${ip} को unblock करना चाहते हैं?`)) return;
    try {
      await adminApi.post(`/admin/unblock-ip`, { ip });
      toast.success(`✅ IP ${ip} unblocked`);
      fetchSuspiciousLogins();
      fetchSuspiciousIPs();
    } catch (e) {
      toast.error("Failed to unblock IP");
    }
  };

  // Support Chat Functions
  const openChat = async (chat) => {
    setSelectedChat(chat);
    try {
      const res = await adminApi.get(`/support/messages/${chat.id}?mark_read=admin`);
      setChatMessages(res.data);
    } catch (e) { toast.error("Failed to load messages"); }
  };

  const sendAdminReply = async () => {
    if (!adminReply.trim() || !selectedChat) return;
    setSendingReply(true);
    try {
      await adminApi.post(`/admin/support/reply/${selectedChat.id}`, {
        message: adminReply,
        message_type: "text"
      });
      setAdminReply("");
      const res = await adminApi.get(`/support/messages/${selectedChat.id}?mark_read=admin`);
      setChatMessages(res.data);
      fetchData();
    } catch (e) { toast.error("Failed to send"); }
    setSendingReply(false);
  };

  const deleteChat = async (chatId) => {
    if (!window.confirm("Delete this chat? All messages will be removed to free space.")) return;
    try {
      await adminApi.delete(`/admin/support/chat/${chatId}`);
      toast.success("Chat deleted");
      setSelectedChat(null);
      setChatMessages([]);
      fetchData();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const closeChat = async (chatId) => {
    try {
      await adminApi.put(`/admin/support/close/${chatId}`);
      toast.success("Chat closed");
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  // Balance Adjustment Functions
  const openBalanceAdjust = (user) => {
    setBalanceUser(user);
    setBalanceForm({ balance_type: "usdt_balance", amount: "", reason: "" });
  };

  const adjustBalance = async () => {
    if (!balanceForm.amount || !balanceForm.reason) {
      toast.error("Amount and reason required");
      return;
    }
    setAdjustingBalance(true);
    try {
      const res = await adminApi.post(`/admin/adjust-balance`, {
        user_id: balanceUser.id,
        balance_type: balanceForm.balance_type,
        amount: parseFloat(balanceForm.amount),
        reason: balanceForm.reason
      });
      toast.success(`✅ ${res.data.balance_type}: ${res.data.previous} → ${res.data.new_balance}`);
      setBalanceUser(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to adjust");
    }
    setAdjustingBalance(false);
  };

  // Login Logs Function
  const viewLoginLogs = async (user) => {
    setLogsUser(user);
    try {
      const res = await adminApi.get(`/admin/login-logs/${user.id}?limit=20`);
      setLoginLogs(res.data);
    } catch (e) {
      toast.error("Failed to load logs");
      setLoginLogs([]);
    }
  };

  // Admin Unstake Functions
  const openUnstake = (user) => {
    setUnstakeUser(user);
    setUnstakeForm({ ltg_amount: "", reason: "" });
  };

  const handleUnstake = async () => {
    if (!unstakeForm.ltg_amount || parseFloat(unstakeForm.ltg_amount) <= 0) {
      toast.error("Enter valid LTG amount");
      return;
    }
    setUnstaking(true);
    try {
      const res = await adminApi.post(`/admin/unstake-user`, {
        user_id: unstakeUser.id,
        ltg_amount: parseFloat(unstakeForm.ltg_amount),
        reason: unstakeForm.reason || "Admin unstake"
      });
      toast.success(`✅ ${res.data.message}`);
      setUnstakeUser(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unstake failed");
    }
    setUnstaking(false);
  };

  const viewReferrals = async (user) => {
    setViewingReferrals(user);
    setLoadingReferrals(true);
    try {
      const res = await adminApi.get(`/admin/user/${user.id}/referrals`);
      setReferralsList(res.data.referrals || []);
    } catch (e) {
      toast.error("Failed to load referrals");
      setReferralsList([]);
    } finally {
      setLoadingReferrals(false);
    }
  };

  // Login as User - View user's full account
  const loginAsUser = async (user) => {
    setViewingAsUser(user);
    setLoadingUserView(true);
    try {
      const res = await adminApi.get(`/admin/login-as-user/${user.id}`);
      setUserViewData(res.data);
    } catch (e) {
      toast.error("Failed to load user data");
      setUserViewData(null);
    } finally {
      setLoadingUserView(false);
    }
  };

  const toggleBlock = async (phone, isBlocked) => {
    try {
      if (isBlocked) {
        await adminApi.post(`/admin/unblock-user/${phone}`);
        toast.success("Unblocked");
      } else {
        await adminApi.post(`/admin/block-user`, { phone });
        toast.success("Blocked");
      }
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  // Investigation Search Function
  const handleInvestigate = async () => {
    if (!investigateQuery.trim()) return;
    setInvestigating(true);
    setInvestigateResults(null);
    try {
      const res = await adminApi.post('/admin/investigate', { query: investigateQuery });
      setInvestigateResults(res.data);
      if (res.data.count === 0) {
        toast.info("No results found - try different keywords");
      } else {
        toast.success(`Found ${res.data.count} results`);
      }
    } catch (e) {
      toast.error("Investigation failed");
      console.error(e);
    } finally {
      setInvestigating(false);
    }
  };

  // Financial Report Functions
  const fetchFinancialReport = async (single = true) => {
    setLoadingReport(true);
    try {
      let url = `/admin/financial-report`;
      if (single && reportDate) {
        url += `?date=${reportDate}`;
      } else if (reportStartDate && reportEndDate) {
        url += `?start_date=${reportStartDate}&end_date=${reportEndDate}`;
      }
      const res = await adminApi.get(url);
      setFinancialReport(res.data);
    } catch (e) {
      toast.error("Failed to load report");
    } finally {
      setLoadingReport(false);
    }
  };

  const downloadReportCSV = async () => {
    try {
      let url = `/admin/financial-report/csv`;
      if (reportDate) {
        url += `?date=${reportDate}`;
      } else if (reportStartDate && reportEndDate) {
        url += `?start_date=${reportStartDate}&end_date=${reportEndDate}`;
      }
      const res = await adminApi.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `financial_report_${reportDate || reportStartDate || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success("CSV downloaded!");
    } catch (e) {
      toast.error("Failed to download CSV");
    }
  };

  // Bulk Selection Functions
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const bulkBlockUsers = async () => {
    if (selectedUsers.length === 0) {
      toast.error("कोई user select नहीं किया");
      return;
    }
    if (!window.confirm(`क्या आप ${selectedUsers.length} users को block करना चाहते हैं?`)) return;
    
    setBulkActionLoading(true);
    try {
      const res = await adminApi.post(`/admin/bulk-block-users`, {
        user_ids: selectedUsers,
        reason: "Bulk blocked by admin"
      });
      toast.success(`✅ ${res.data.message}`);
      setSelectedUsers([]);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to block users");
    }
    setBulkActionLoading(false);
  };

  const bulkUnblockUsers = async () => {
    if (selectedUsers.length === 0) {
      toast.error("कोई user select नहीं किया");
      return;
    }
    if (!window.confirm(`क्या आप ${selectedUsers.length} users को unblock करना चाहते हैं?`)) return;
    
    setBulkActionLoading(true);
    try {
      const res = await adminApi.post(`/admin/bulk-unblock-users`, {
        user_ids: selectedUsers
      });
      toast.success(`✅ ${res.data.message}`);
      setSelectedUsers([]);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to unblock users");
    }
    setBulkActionLoading(false);
  };

  const bulkDeleteUsers = async () => {
    if (selectedUsers.length === 0) {
      toast.error("कोई user select नहीं किया");
      return;
    }
    if (!window.confirm(`⚠️ WARNING: क्या आप ${selectedUsers.length} users को PERMANENTLY DELETE करना चाहते हैं? यह undo नहीं होगा!`)) return;
    if (!window.confirm(`FINAL CONFIRMATION: ${selectedUsers.length} users और उनका सारा data हमेशा के लिए delete हो जाएगा!`)) return;
    
    setBulkActionLoading(true);
    try {
      const res = await adminApi.post(`/admin/bulk-delete-users`, {
        user_ids: selectedUsers
      });
      toast.success(`✅ ${res.data.message}`);
      setSelectedUsers([]);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete users");
    }
    setBulkActionLoading(false);
  };

  const handleTx = async (txId, action) => {
    // Optimistic UI - remove from list immediately
    setPendingTx(prev => prev.filter(tx => tx.id !== txId));
    toast.success(action === "approve" ? "✅ Approved" : "❌ Rejected");
    
    try {
      await adminApi.post(`/admin/${action}-transaction/${txId}`);
      // Only refresh transactions after 2 seconds delay (let backend process)
      setTimeout(async () => {
        const txRes = await adminApi.get(`/admin/pending-transactions`);
        setPendingTx(txRes.data);
      }, 2000);
    } catch (e) { 
      toast.error("Failed - refreshing...");
      // On error, refresh to get correct state
      const txRes = await adminApi.get(`/admin/pending-transactions`);
      setPendingTx(txRes.data);
    }
  };

  const changePin = async () => {
    if (!oldPin) { toast.error("Enter current PIN"); return; }
    if (!newPin || newPin.length !== 8 || !/^\d{8}$/.test(newPin)) { toast.error("New PIN must be exactly 8 digits"); return; }
    if (newPin !== confirmNewPin) { toast.error("PINs don't match"); return; }
    try {
      await adminApi.post(`/admin/change-pin`, { current_pin: oldPin, new_pin: newPin });
      toast.success("PIN changed successfully!");
      setOldPin(""); setNewPin(""); setConfirmNewPin("");
      fetchSettings(); // Refresh to show new PIN
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change PIN"); }
  };

  const sendBulkEmail = async () => {
    if (!bulkEmailSubject || !bulkEmailMessage) { toast.error("Please fill subject and message"); return; }
    if (!window.confirm(`Send email to all ${users.length} users?`)) return;
    setSendingBulkEmail(true);
    try {
      const res = await adminApi.post(`/admin/bulk-email`, { 
        subject: bulkEmailSubject, 
        message: bulkEmailMessage 
      });
      toast.success(res.data.message);
      setBulkEmailSubject("");
      setBulkEmailMessage("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    setSendingBulkEmail(false);
  };

  const sendAnnouncement = async () => {
    if (!announcementTitle || !announcementMessage) { toast.error("Please fill title and message"); return; }
    if (!window.confirm("Send this announcement to all users?")) return;
    setSendingAnnouncement(true);
    try {
      await adminApi.post(`/admin/announcement`, { 
        title: announcementTitle, 
        message: announcementMessage,
        is_active: true
      });
      toast.success("📢 Announcement sent successfully!");
      setAnnouncementTitle("");
      setAnnouncementMessage("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    setSendingAnnouncement(false);
  };

  const stopAnnouncement = async () => {
    if (!window.confirm("Stop this announcement? It will no longer show to users.")) return;
    try {
      await adminApi.put(`/admin/announcement/deactivate`);
      toast.success("🛑 Announcement stopped!");
      setActiveAnnouncement(null);
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  // Promo Code Functions
  const createPromoCode = async () => {
    if (!promoForm.code || !promoForm.bonus_amount) { toast.error("Fill code and amount"); return; }
    try {
      await adminApi.post(`/admin/promo-code`, {
        code: promoForm.code,
        bonus_type: promoForm.bonus_type,
        bonus_amount: parseFloat(promoForm.bonus_amount),
        max_uses: parseInt(promoForm.max_uses) || 100,
        expires_at: promoForm.expires_at || null
      });
      toast.success("🎁 Promo code created!");
      setPromoForm({ code: "", bonus_type: "ltg", bonus_amount: "", max_uses: "100", expires_at: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deletePromoCode = async (code) => {
    if (!window.confirm(`Delete promo code ${code}?`)) return;
    try {
      await adminApi.delete(`/admin/promo-code/${code}`);
      toast.success("Promo code deleted");
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  const togglePromoCode = async (code) => {
    try {
      await adminApi.put(`/admin/promo-code/${code}/toggle`);
      toast.success("Status updated");
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  // Notification Functions
  const sendNotification = async () => {
    if (!notifForm.title || !notifForm.message) { toast.error("Fill title and message"); return; }
    setSendingNotif(true);
    try {
      const payload = {
        title: notifForm.title,
        message: notifForm.message,
        notification_type: notifForm.notification_type
      };
      if (notifForm.user_id) payload.user_id = notifForm.user_id;
      
      await adminApi.post(`/admin/notification`, payload);
      toast.success(notifForm.user_id ? "🔔 Notification sent to user!" : "🔔 Notification sent to all users!");
      setNotifForm({ user_id: "", title: "", message: "", notification_type: "info" });
    } catch (e) { toast.error("Failed"); }
    setSendingNotif(false);
  };

  // Email Template Functions
  const loadTemplate = async (templateName) => {
    setSelectedTemplate(templateName);
    try {
      const res = await adminApi.get(`/admin/email-template/${templateName}`);
      setTemplateForm({ subject: res.data.subject || "", body: res.data.body || "" });
    } catch (e) { setTemplateForm({ subject: "", body: "" }); }
  };

  const saveTemplate = async () => {
    if (!templateForm.subject || !templateForm.body) { toast.error("Fill subject and body"); return; }
    setSavingTemplate(true);
    try {
      await adminApi.post(`/admin/email-template`, {
        template_name: selectedTemplate,
        subject: templateForm.subject,
        body: templateForm.body
      });
      toast.success("📧 Template saved!");
      fetchData();
    } catch (e) { toast.error("Failed"); }
    setSavingTemplate(false);
  };

  const saveReferralBonusPercent = async () => {
    setSavingReferralBonus(true);
    try {
      await adminApi.post(`/admin/referral-bonus-settings`, { percentage: parseFloat(referralBonusPercent) });
      toast.success(`✅ Referral bonus updated to ${referralBonusPercent}%`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSavingReferralBonus(false);
  };

  const clearPendingBonus = async (userId, userName) => {
    if (!window.confirm(`क्या आप "${userName}" का pending bonus clear करना चाहते हैं? इससे इस user के लिए referrer को bonus नहीं मिलेगा।`)) return;
    try {
      await adminApi.delete(`/admin/pending-bonus/${userId}`);
      toast.success(`✅ Pending bonus cleared for ${userName}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to clear"); }
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setEditForm({ phone: u.phone, name: u.full_name, pin: "" });
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This cannot be undone!`)) return;
    try {
      await adminApi.delete(`/admin/user/${userId}`);
      toast.success(`${userName} deleted!`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const saveUserChanges = async () => {
    try {
      await adminApi.post(`/admin/change-user-details?phone=${editingUser.phone}&new_phone=${editForm.phone}&new_name=${editForm.name}${editForm.pin ? `&new_pin=${editForm.pin}` : ""}`);
      toast.success("User updated!");
      setEditingUser(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copy = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success("Copied!");
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success("Copied!");
      }
    } catch (err) {
      // Fallback method
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success("Copied!");
      } catch (e) {
        toast.error("Copy failed - please copy manually");
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="min-h-screen p-4" data-testid="admin-page">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => { logout(); navigate("/"); }}><ChevronLeft size={24} /></button>
        <h1 className="font-manrope font-bold text-xl text-primary">{t("admin")}</h1>
        <button onClick={() => { fetchData(); fetchLiveUsers(); }}><RefreshCw size={20} /></button>
      </div>

      {/* Live Users Banner */}
      <div 
        onClick={() => setShowLiveUsers(!showLiveUsers)}
        className="mb-4 p-3 bg-gradient-to-r from-green-900/50 to-emerald-900/50 border border-green-500/30 rounded-xl cursor-pointer hover:border-green-500/50 transition-all"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity size={18} className="text-green-500 animate-pulse" />
            <span className="text-green-400 font-bold">Live Users:</span>
            <span className="text-white text-xl font-bold">{liveUsers.count}</span>
          </div>
          <span className="text-muted-foreground text-xs">{showLiveUsers ? '▲ Hide' : '▼ View'}</span>
        </div>
        
        {showLiveUsers && liveUsers.users.length > 0 && (
          <div className="mt-3 pt-3 border-t border-green-500/20 space-y-2 max-h-48 overflow-y-auto">
            {liveUsers.users.map((u, i) => (
              <div key={i} className="flex items-center justify-between bg-black/30 p-2 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  <span className="text-white text-sm">{u.full_name}</span>
                </div>
                <div className="text-right">
                  <p className="text-muted-foreground text-xs">{u.phone}</p>
                  <p className="text-primary text-xs">{(u.staked_amount || 0).toLocaleString()} LTG</p>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {showLiveUsers && liveUsers.users.length === 0 && (
          <p className="mt-3 pt-3 border-t border-green-500/20 text-muted-foreground text-center text-sm">No users online right now</p>
        )}
      </div>

      {/* Tabs - Row 1 */}
      <div className="flex gap-2 mb-2">
        {[
          { key: "users", icon: Users, label: t("users") },
          { key: "transactions", icon: FileText, label: t("transactions") },
          { key: "settings", icon: Settings, label: t("settings") }
        ].map(item => (
          <button
            key={item.key}
            onClick={() => setTab(item.key)}
            data-testid={`admin-tab-${item.key}`}
            className={`flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm ${
              tab === item.key 
                ? "bg-primary text-black" 
                : "bg-card"
            }`}
          >
            <item.icon size={16} /> {item.label}
          </button>
        ))}
      </div>
      
      {/* Tabs - Row 2 (New Features) */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { key: "suspicious", icon: Shield, label: "🚨 IPs" },
          { key: "investigate", icon: Search, label: "🔍 Investigate" },
          { key: "support", icon: MessageCircle, label: "Support" },
          { key: "promo", icon: Gift, label: "Promo" },
          { key: "notifications", icon: Bell, label: "Notify" },
          { key: "templates", icon: FileText, label: "Templates" },
          { key: "reports", icon: Activity, label: "Reports" },
          { key: "games", icon: Gamepad2, label: "🎮 Games" },
          { key: "analytics", icon: BarChart3, label: "📊 Analytics" },
          { key: "p2p", icon: ShieldCheck, label: "💱 P2P" }
        ].map(item => (
          <button
            key={item.key}
            onClick={() => setTab(item.key)}
            data-testid={`admin-tab-${item.key}`}
            className={`flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2 text-xs ${tab === item.key ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white" : "bg-card"}`}
          >
            <item.icon size={14} /> {item.label}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {tab === "users" && (
        <div className="space-y-3">
          {/* Search Box */}
          <div className="relative">
            <input
              type="text"
              placeholder="🔍 Search by name, phone or email..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="input-dark w-full pl-4 pr-10"
              data-testid="admin-user-search"
            />
            {searchQuery && (
              <button 
                onClick={() => { setSearchQuery(""); setCurrentPage(1); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
              >
                ✕
              </button>
            )}
          </div>

          {/* Sort Toggle & Stats */}
          <div className="flex flex-wrap justify-between items-center gap-2">
            <p className="text-green-500">
              {t("users")}: {totalUsers} 
              {searchQuery && <span className="text-yellow-400 text-xs ml-1">(filtered)</span>}
              {loadingUsers && <span className="text-yellow-400 text-xs ml-2">⏳ Loading...</span>}
            </p>
            <div className="flex gap-2 flex-wrap">
              <button 
                onClick={() => { setSortBy("name"); setCurrentPage(1); }}
                disabled={loadingUsers}
                className={`px-3 py-1 rounded-full text-xs transition-all ${sortBy === "name" ? "bg-primary text-black" : "bg-card"} ${loadingUsers ? "opacity-50" : ""}`}
              >
                A-Z Name
              </button>
              <button 
                onClick={() => { setSortBy("stake"); setCurrentPage(1); }}
                disabled={loadingUsers}
                className={`px-3 py-1 rounded-full text-xs transition-all ${sortBy === "stake" ? "bg-primary text-black" : "bg-card"} ${loadingUsers ? "opacity-50" : ""}`}
              >
                High Stake
              </button>
              <button 
                onClick={() => { setSortBy("newest"); setCurrentPage(1); }}
                disabled={loadingUsers}
                className={`px-3 py-1 rounded-full text-xs transition-all ${sortBy === "newest" ? "bg-primary text-black" : "bg-card"} ${loadingUsers ? "opacity-50" : ""}`}
              >
                Newest
              </button>
            </div>
          </div>
          
          {/* Pagination Info */}
          <div className="flex justify-between items-center bg-card/50 rounded-lg px-3 py-2">
            <span className="text-muted-foreground text-sm">
              Page {currentPage} of {totalPages} ({totalUsers} users)
            </span>
            <span className="text-xs text-muted-foreground">
              {usersPerPage} per page
            </span>
          </div>

          {/* Bulk Selection Bar - Shows when search is active */}
          {searchQuery && users.length > 0 && (
            <div className="bg-gradient-to-r from-red-900/50 to-orange-900/50 border border-red-500/30 rounded-xl p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === users.length && users.length > 0}
                      onChange={selectAllUsers}
                      className="w-5 h-5 accent-primary"
                    />
                    <span className="text-sm">
                      {selectedUsers.length === users.length ? "Unselect All" : "Select All"} ({users.length})
                    </span>
                  </label>
                  {selectedUsers.length > 0 && (
                    <span className="bg-primary text-black px-2 py-1 rounded-full text-xs font-bold">
                      {selectedUsers.length} selected
                    </span>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={bulkBlockUsers}
                    disabled={selectedUsers.length === 0 || bulkActionLoading}
                    className="px-4 py-2 bg-red-600 rounded-lg text-sm font-bold disabled:opacity-50 flex items-center gap-1"
                  >
                    <Lock size={14} /> Block All
                  </button>
                  <button
                    onClick={bulkUnblockUsers}
                    disabled={selectedUsers.length === 0 || bulkActionLoading}
                    className="px-4 py-2 bg-green-600 rounded-lg text-sm font-bold disabled:opacity-50"
                  >
                    Unblock All
                  </button>
                  <button
                    onClick={bulkDeleteUsers}
                    disabled={selectedUsers.length === 0 || bulkActionLoading}
                    className="px-4 py-2 bg-red-900 rounded-lg text-sm font-bold disabled:opacity-50 flex items-center gap-1"
                  >
                    <Trash2 size={14} /> Delete All
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {users.map(u => (
            <div key={u.id} className={`admin-user-card ${selectedUsers.includes(u.id) ? 'border-primary bg-primary/10' : ''}`}>
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3">
                  {/* Checkbox for bulk selection - only show when searching */}
                  {searchQuery && (
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(u.id)}
                      onChange={() => toggleUserSelection(u.id)}
                      className="w-5 h-5 mt-1 accent-primary cursor-pointer"
                    />
                  )}
                  <div>
                    <span className="text-yellow-500 text-xs font-bold mr-2">#{u.serial_number || '-'}</span>
                    <span className="font-bold">{u.full_name}</span>
                    {u.is_blocked && <span className="ml-2 text-red-500 text-xs">🚫 BLOCKED</span>}
                    <p className="text-muted-foreground text-sm">{u.email}</p>
                    <p className="text-muted-foreground text-sm">{u.phone} • {u.country}</p>
                    <p className="text-blue-400 text-xs">PIN: {u.pin}</p>
                    {u.referred_by_name && (
                      <p className="text-purple-400 text-xs">👤 Referred by: {u.referred_by_name}</p>
                    )}
                    {u.referrals_count > 0 && (
                      <p className="text-green-400 text-xs">🎯 Referrals: {u.referrals_count}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => openEditUser(u)}
                    className="px-3 py-1 bg-blue-600 rounded-full text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => openBalanceAdjust(u)}
                    className="px-3 py-1 bg-yellow-600 rounded-full text-sm"
                  >
                    💰 Balance
                  </button>
                  <button
                    onClick={() => openUnstake(u)}
                    className="px-3 py-1 bg-orange-600 rounded-full text-sm"
                  >
                    🔓 Unstake
                  </button>
                  <button
                    onClick={() => viewLoginLogs(u)}
                    className="px-3 py-1 bg-cyan-600 rounded-full text-sm"
                  >
                    📋 Logs
                  </button>
                  <button
                    onClick={() => viewReferrals(u)}
                    className="px-3 py-1 bg-purple-600 rounded-full text-sm"
                  >
                    👥 Referrals
                  </button>
                  <button
                    onClick={() => loginAsUser(u)}
                    className="px-3 py-1 bg-emerald-600 rounded-full text-sm"
                  >
                    👁️ View
                  </button>
                  <button
                    onClick={() => toggleBlock(u.phone, u.is_blocked)}
                    className={`px-3 py-1 rounded-full text-sm ${u.is_blocked ? "bg-green-600" : "bg-red-600"}`}
                  >
                    {u.is_blocked ? t("unblock") : t("block")}
                  </button>
                  <button
                    onClick={() => deleteUser(u.id, u.full_name)}
                    className="px-3 py-1 bg-red-800 rounded-full text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <p className="text-yellow-500 text-sm mt-2">USDT: ${u.usdt_balance.toFixed(4)} | LTG: {u.ltg_balance.toFixed(0)}</p>
              <p className="text-green-500 text-xs">Staked: {u.staked_amount.toFixed(0)} | Reward: ${(u.staked_reward / 1000).toFixed(4)}</p>
              <p className="text-muted-foreground text-xs mt-1">{u.wallet_address}</p>
            </div>
          ))}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4 pb-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-card rounded-lg text-sm disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary hover:text-black transition-all"
              >
                ⏮️ First
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-card rounded-lg text-sm disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary hover:text-black transition-all"
              >
                ← Prev
              </button>
              
              {/* Page Numbers */}
              <div className="flex gap-1">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${
                        currentPage === pageNum 
                          ? "bg-primary text-black" 
                          : "bg-card hover:bg-primary/20"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-card rounded-lg text-sm disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary hover:text-black transition-all"
              >
                Next →
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 bg-card rounded-lg text-sm disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary hover:text-black transition-all"
              >
                Last ⏭️
              </button>
            </div>
          )}

          {users.length === 0 && (
            <p className="text-muted-foreground text-center py-8">No users found</p>
          )}
        </div>
      )}

      {/* Suspicious IPs Tab */}
      {tab === "suspicious" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-manrope font-bold text-xl text-red-400">🚨 Suspicious IPs (Multiple Accounts)</h2>
            <button 
              onClick={fetchSuspiciousIPs}
              disabled={loadingSuspiciousIPs}
              className="px-4 py-2 bg-red-600 rounded-lg text-sm font-bold disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw size={16} className={loadingSuspiciousIPs ? "animate-spin" : ""} />
              {loadingSuspiciousIPs ? "Loading..." : "Scan IPs"}
            </button>
          </div>

          {/* Search User by Phone/Email */}
          <div className="bg-card rounded-xl p-4 border border-primary/30">
            <p className="text-sm font-bold mb-2 text-primary">🔍 Search User's IP</p>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Phone या Email enter करें..."
                value={ipSearchQuery}
                onChange={(e) => setIpSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchUserIP()}
                className="flex-1 px-3 py-2 bg-black/30 border border-border rounded-lg text-sm"
              />
              <button
                onClick={searchUserIP}
                disabled={searchingIP}
                className="px-4 py-2 bg-primary text-black rounded-lg text-sm font-bold disabled:opacity-50"
              >
                {searchingIP ? "..." : "Search"}
              </button>
            </div>
            
            {/* Search Result */}
            {ipSearchResult && (
              <div className="mt-4 p-3 bg-black/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-bold text-white">{ipSearchResult.user.full_name}</p>
                    <p className="text-xs text-muted-foreground">{ipSearchResult.user.email}</p>
                    <p className="text-xs text-muted-foreground">{ipSearchResult.user.phone}</p>
                  </div>
                  {(ipSearchResult.recent_login_ip || ipSearchResult.registration_ip) && (
                    <div className={`px-3 py-1 rounded-full text-sm font-bold ${ipSearchResult.totalOnIP > 1 ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                      {ipSearchResult.totalOnIP} {ipSearchResult.totalOnIP > 1 ? 'accounts' : 'account'}
                    </div>
                  )}
                </div>
                
                {(ipSearchResult.recent_login_ip || ipSearchResult.registration_ip) ? (
                  <>
                    {/* Recent Login IP - TOP (Clickable) */}
                    {ipSearchResult.recent_login_ip && (
                      <div className="mb-2 p-2 bg-green-900/30 rounded-lg border border-green-500/30">
                        <p className="text-xs text-green-400 font-bold mb-1">📱 Recent Login IP:</p>
                        <button 
                          onClick={async () => {
                            setLoadingLocation(true);
                            const loc = await fetchIPLocation(ipSearchResult.recent_login_ip);
                            setIpLocationModal(loc);
                            setLoadingLocation(false);
                          }}
                          className="font-mono text-yellow-400 hover:text-yellow-300 underline text-lg"
                        >
                          {ipSearchResult.recent_login_ip} {loadingLocation ? '⏳' : '📍'}
                        </button>
                        {ipSearchResult.recent_login_time && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Last login: {new Date(ipSearchResult.recent_login_time).toLocaleString('en-IN')}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Registration IP - BELOW */}
                    {ipSearchResult.registration_ip && (
                      <div className="mb-2 p-2 bg-blue-900/20 rounded-lg border border-blue-500/20">
                        <p className="text-xs text-blue-400 mb-1">📝 Registration IP:</p>
                        <button 
                          onClick={async () => {
                            setLoadingLocation(true);
                            const loc = await fetchIPLocation(ipSearchResult.registration_ip);
                            setIpLocationModal(loc);
                            setLoadingLocation(false);
                          }}
                          className="font-mono text-gray-400 hover:text-yellow-300 underline"
                        >
                          {ipSearchResult.registration_ip} 📍
                        </button>
                      </div>
                    )}

                    {/* No Registration IP message */}
                    {!ipSearchResult.registration_ip && ipSearchResult.recent_login_ip && (
                      <p className="text-xs text-yellow-400 mb-2">⚠️ Registration IP नहीं मिला (पुराना account)</p>
                    )}

                    {/* Location Details (if already fetched) */}
                    {ipSearchResult.location && (
                      <div className="mb-3 p-2 bg-green-900/20 rounded-lg border border-green-500/30">
                        <p className="text-xs text-green-400 font-bold mb-1">📍 Location:</p>
                        <p className="text-sm text-white">{ipSearchResult.location.city}, {ipSearchResult.location.region}</p>
                        <p className="text-xs text-muted-foreground">{ipSearchResult.location.country}</p>
                        <p className="text-xs text-muted-foreground">ISP: {ipSearchResult.location.isp}</p>
                        <p className="text-xs text-muted-foreground">
                          Coordinates: {ipSearchResult.location.latitude}, {ipSearchResult.location.longitude}
                        </p>
                        <a 
                          href={ipSearchResult.location.google_maps_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:underline"
                        >
                          🗺️ Open in Google Maps
                        </a>
                      </div>
                    )}

                    {/* Login History with clickable IPs */}
                    {ipSearchResult.login_history?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-muted-foreground mb-1">📋 Login History (click IP for location):</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {ipSearchResult.login_history.slice(0, 5).map((log, idx) => (
                            <div key={idx} className={`flex items-center justify-between p-1.5 rounded text-xs ${log.success ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                              <button 
                                onClick={async () => {
                                  if (log.ip) {
                                    setLoadingLocation(true);
                                    const loc = await fetchIPLocation(log.ip);
                                    setIpLocationModal(loc);
                                    setLoadingLocation(false);
                                  }
                                }}
                                className="font-mono text-yellow-400 hover:underline"
                              >
                                {log.ip} 📍
                              </button>
                              <span className={log.success ? 'text-green-400' : 'text-red-400'}>
                                {log.success ? '✓' : '✗'} {new Date(log.created_at).toLocaleDateString('en-IN')}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Other users on same IP */}
                    {ipSearchResult.totalOnIP > 1 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs text-red-400 font-bold">⚠️ इस IP पर {ipSearchResult.totalOnIP} accounts हैं:</p>
                        {ipSearchResult.otherUsers.map((u, idx) => (
                          <div key={u.id || idx} className="flex items-center justify-between bg-red-900/20 p-2 rounded text-xs">
                            <div>
                              <span className="text-white">{u.full_name}</span>
                              <span className="text-muted-foreground ml-2">{u.email || u.phone}</span>
                            </div>
                            <span className={`px-2 py-0.5 rounded ${u.is_blocked ? 'bg-red-500/30 text-red-400' : 'bg-green-500/30 text-green-400'}`}>
                              {u.is_blocked ? 'Blocked' : 'Active'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-yellow-400">{ipSearchResult.message || "No IP found"}</p>
                )}
              </div>
            )}
          </div>

          {!suspiciousIPs ? (
            <div className="bg-card rounded-xl p-8 text-center">
              <Shield size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">Click "Scan IPs" to detect suspicious activity</p>
              <p className="text-xs text-muted-foreground">यह scan करेगा कि कौन से IP से multiple accounts बने हैं</p>
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-500/20 border border-blue-500/30 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-blue-400">{suspiciousIPs.total_unique_ips}</p>
                  <p className="text-xs text-muted-foreground">Total Unique IPs</p>
                </div>
                <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-red-400">{suspiciousIPs.duplicate_ip_count}</p>
                  <p className="text-xs text-muted-foreground">Suspicious IPs</p>
                </div>
                <div className="bg-orange-500/20 border border-orange-500/30 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-orange-400">{suspiciousIPs.duplicate_accounts_count}</p>
                  <p className="text-xs text-muted-foreground">Accounts from Dup IPs</p>
                </div>
              </div>

              {/* IP List */}
              <div className="space-y-3">
                {Object.entries(suspiciousIPs.duplicates || {}).length === 0 ? (
                  <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-6 text-center">
                    <p className="text-green-400 font-bold">✅ कोई suspicious IP नहीं मिला!</p>
                    <p className="text-xs text-muted-foreground mt-2">सभी IPs से सिर्फ 1 account बना है</p>
                  </div>
                ) : (
                  Object.entries(suspiciousIPs.duplicates).map(([ip, ipUsers]) => (
                    <div key={ip} className="bg-red-900/20 border border-red-500/30 rounded-xl overflow-hidden">
                      {/* IP Header */}
                      <div 
                        onClick={() => setExpandedIP(expandedIP === ip ? null : ip)}
                        className="p-4 cursor-pointer hover:bg-red-900/30 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                            <span className="text-red-400 font-bold">{ipUsers.length}</span>
                          </div>
                          <div>
                            <p className="font-mono text-red-400 font-bold">{ip}</p>
                            <p className="text-xs text-muted-foreground">{ipUsers.length} accounts from this IP</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              blockIPUsers(ip, ipUsers.map(u => u.id));
                            }}
                            className="px-3 py-1.5 bg-red-600 rounded-lg text-xs font-bold hover:bg-red-500"
                          >
                            🚫 Block All
                          </button>
                          <span className="text-muted-foreground">{expandedIP === ip ? '▲' : '▼'}</span>
                        </div>
                      </div>

                      {/* Expanded Users List */}
                      {expandedIP === ip && (
                        <div className="border-t border-red-500/30 p-3 space-y-2 bg-black/20">
                          {ipUsers.map((u, idx) => (
                            <div key={u.id || idx} className="flex items-center justify-between bg-black/30 rounded-lg p-3">
                              <div>
                                <p className="font-bold text-white">{u.full_name}</p>
                                <p className="text-xs text-muted-foreground">{u.email}</p>
                                <p className="text-xs text-muted-foreground">{u.phone}</p>
                                <p className="text-xs text-muted-foreground">
                                  {u.created_at ? new Date(u.created_at).toLocaleString('en-IN') : 'N/A'}
                                </p>
                              </div>
                              <div className="flex flex-col gap-1 items-end">
                                <span className={`px-2 py-0.5 rounded-full text-xs ${u.is_blocked ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                                  {u.is_blocked ? '🚫 Blocked' : '✅ Active'}
                                </span>
                                {!u.is_blocked && (
                                  <button
                                    onClick={() => blockIPUsers(ip, [u.id])}
                                    className="px-2 py-1 bg-red-600/50 rounded text-xs hover:bg-red-600"
                                  >
                                    Block
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {/* Suspicious Login Activity Section */}
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-manrope font-bold text-lg text-orange-400">🔐 Suspicious Login Activity (24h)</h3>
              <button 
                onClick={fetchSuspiciousLogins}
                disabled={loadingSuspiciousLogins}
                className="px-4 py-2 bg-orange-600 rounded-lg text-sm font-bold disabled:opacity-50 flex items-center gap-2"
              >
                <RefreshCw size={16} className={loadingSuspiciousLogins ? "animate-spin" : ""} />
                {loadingSuspiciousLogins ? "Loading..." : "Scan Logins"}
              </button>
            </div>

            {!suspiciousLogins ? (
              <div className="bg-card rounded-xl p-6 text-center">
                <Lock size={36} className="mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground text-sm">Click "Scan Logins" to check login activity</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Failed Login Attempts */}
                <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
                  <h4 className="font-bold text-red-400 mb-3">❌ Failed Login IPs ({suspiciousLogins.failed_login_ips?.length || 0})</h4>
                  {suspiciousLogins.failed_login_ips?.length === 0 ? (
                    <p className="text-green-400 text-sm">✅ कोई suspicious failed login नहीं</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {suspiciousLogins.failed_login_ips?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-black/30 p-3 rounded-lg">
                          <div>
                            <p className="font-mono text-red-400">{item.ip}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.failed_count} failed attempts | Targeted: {item.targeted_users?.slice(0, 3).join(", ")}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {item.is_blocked ? (
                              <button onClick={() => unblockIP(item.ip)} className="px-3 py-1 bg-green-600 rounded text-xs">Unblock</button>
                            ) : (
                              <button onClick={() => blockIP(item.ip, "Multiple failed login attempts")} className="px-3 py-1 bg-red-600 rounded text-xs">Block IP</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Multi-Account Logins */}
                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4">
                  <h4 className="font-bold text-yellow-400 mb-3">👥 Multi-Account Login IPs ({suspiciousLogins.multi_account_ips?.length || 0})</h4>
                  {suspiciousLogins.multi_account_ips?.length === 0 ? (
                    <p className="text-green-400 text-sm">✅ कोई multi-account login नहीं</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {suspiciousLogins.multi_account_ips?.map((item, idx) => (
                        <div key={idx} className="bg-black/30 p-3 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="font-mono text-yellow-400">{item.ip}</p>
                              <p className="text-xs text-muted-foreground">
                                {item.account_count} accounts | {item.login_count} logins
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {item.is_blocked ? (
                                <button onClick={() => unblockIP(item.ip)} className="px-3 py-1 bg-green-600 rounded text-xs">Unblock</button>
                              ) : (
                                <button onClick={() => blockIP(item.ip, "Multiple account logins")} className="px-3 py-1 bg-red-600 rounded text-xs">Block IP</button>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {item.user_details?.map((u, i) => (
                              <span key={i} className={`px-2 py-0.5 rounded text-xs ${u.is_blocked ? 'bg-red-500/30 text-red-400' : 'bg-blue-500/30 text-blue-400'}`}>
                                {u.full_name || u.email}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Blocked IPs List */}
                {suspiciousLogins.blocked_ips?.length > 0 && (
                  <div className="bg-gray-900/50 border border-gray-500/30 rounded-xl p-4">
                    <h4 className="font-bold text-gray-400 mb-3">🚫 Blocked IPs ({suspiciousLogins.blocked_ips?.length})</h4>
                    <div className="flex flex-wrap gap-2">
                      {suspiciousLogins.blocked_ips?.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-black/30 px-3 py-1 rounded-full">
                          <span className="font-mono text-xs text-gray-400">{item.ip}</span>
                          <button onClick={() => unblockIP(item.ip)} className="text-green-400 text-xs hover:underline">×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <>
          <div className="modal-overlay" onClick={() => setEditingUser(null)}></div>
          <div className="modal-content">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-manrope font-bold text-xl">Edit User</h2>
              <button onClick={() => setEditingUser(null)}><X size={24} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-muted-foreground text-sm">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="input-dark"
                />
              </div>
              <div>
                <label className="text-muted-foreground text-sm">Phone</label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="input-dark"
                />
              </div>
              <div>
                <label className="text-muted-foreground text-sm">New PIN (leave empty to keep current)</label>
                <input
                  type="password"
                  placeholder="8-digit PIN"
                  value={editForm.pin}
                  onChange={(e) => setEditForm({ ...editForm, pin: e.target.value.replace(/\D/g, "") })}
                  maxLength={8}
                  className="input-dark"
                />
              </div>
              <button onClick={saveUserChanges} className="w-full btn-gold py-3 rounded-full">
                Save Changes
              </button>
            </div>
          </div>
        </>
      )}

      {/* Balance Adjustment Modal */}
      {balanceUser && (
        <>
          <div className="modal-overlay" onClick={() => setBalanceUser(null)}></div>
          <div className="modal-content">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-manrope font-bold text-xl">💰 Adjust Balance</h2>
              <button onClick={() => setBalanceUser(null)}><X size={24} /></button>
            </div>
            
            <div className="bg-black/30 rounded-lg p-3 mb-4">
              <p className="font-bold">{balanceUser.full_name}</p>
              <p className="text-muted-foreground text-sm">{balanceUser.phone}</p>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                <p>USDT: <span className="text-yellow-400">${balanceUser.usdt_balance?.toFixed(2)}</span></p>
                <p>Deposited: <span className="text-green-400">${balanceUser.deposited_usdt?.toFixed(2)}</span></p>
                <p className="col-span-2 bg-green-500/20 p-1 rounded">⭐ LTG Earning (Dashboard): <span className="text-green-400 font-bold">{balanceUser.staked_reward?.toFixed(0) || 0}</span></p>
                <p>LTG (Old): <span className="text-cyan-400">{balanceUser.ltg_balance?.toFixed(0)}</span></p>
                <p>LTG (Sellable): <span className="text-blue-400">{balanceUser.sellable_ltg?.toFixed(0)}</span></p>
                <p>Staked: <span className="text-purple-400">{balanceUser.staked_amount?.toFixed(0)}</span></p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-muted-foreground text-sm">Balance Type</label>
                <select
                  value={balanceForm.balance_type}
                  onChange={(e) => setBalanceForm({ ...balanceForm, balance_type: e.target.value })}
                  className="input-dark w-full"
                >
                  <option value="usdt_balance">USDT Balance (Spendable)</option>
                  <option value="deposited_usdt">Deposited USDT</option>
                  <option value="staked_reward">⭐ LTG Earning (User Dashboard)</option>
                  <option value="ltg_balance">LTG Balance (Old)</option>
                  <option value="sellable_ltg">Sellable LTG (Deposited)</option>
                  <option value="staked_amount">Staked Amount</option>
                </select>
              </div>
              <div>
                <label className="text-muted-foreground text-sm">Amount (+ve to add, -ve to subtract)</label>
                <input
                  type="number"
                  placeholder="e.g. 100 or -50"
                  value={balanceForm.amount}
                  onChange={(e) => setBalanceForm({ ...balanceForm, amount: e.target.value })}
                  className="input-dark"
                />
              </div>
              <div>
                <label className="text-muted-foreground text-sm">Reason (required)</label>
                <input
                  type="text"
                  placeholder="e.g. Bonus, Correction, Refund"
                  value={balanceForm.reason}
                  onChange={(e) => setBalanceForm({ ...balanceForm, reason: e.target.value })}
                  className="input-dark"
                />
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => setBalanceForm({...balanceForm, amount: "100"})}
                  className="px-3 py-2 bg-green-600 rounded-lg text-sm"
                >
                  +100
                </button>
                <button 
                  onClick={() => setBalanceForm({...balanceForm, amount: "-100"})}
                  className="px-3 py-2 bg-red-600 rounded-lg text-sm"
                >
                  -100
                </button>
                <button 
                  onClick={() => setBalanceForm({...balanceForm, amount: "1000"})}
                  className="px-3 py-2 bg-green-600 rounded-lg text-sm"
                >
                  +1000
                </button>
              </div>
              <button 
                onClick={adjustBalance} 
                disabled={adjustingBalance}
                className="w-full btn-gold py-3 rounded-full disabled:opacity-50"
              >
                {adjustingBalance ? "Processing..." : "✅ Adjust Balance"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Admin Unstake Modal */}
      {unstakeUser && (
        <>
          <div className="modal-overlay" onClick={() => setUnstakeUser(null)}></div>
          <div className="modal-content">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-manrope font-bold text-xl">🔓 Admin Unstake</h2>
              <button onClick={() => setUnstakeUser(null)}><X size={24} /></button>
            </div>
            
            <div className="bg-black/30 rounded-lg p-3 mb-4">
              <p className="font-bold">{unstakeUser.full_name}</p>
              <p className="text-muted-foreground text-sm">{unstakeUser.phone}</p>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                <p>Staked LTG: <span className="text-purple-400">{unstakeUser.staked_amount?.toFixed(0) || 0}</span></p>
                <p>USDT Balance: <span className="text-yellow-400">${unstakeUser.usdt_balance?.toFixed(2) || 0}</span></p>
              </div>
            </div>

            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-4">
              <p className="text-orange-400 text-sm">
                ⚠️ Unstake karoge toh LTG se USDT ban jayega aur user ke USDT balance mein aa jayega. 
                User fir Send ya Withdraw kar sakta hai.
              </p>
              <p className="text-orange-300 text-xs mt-1">
                Rate: 1000 LTG = 1 USDT
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-muted-foreground text-sm">LTG Amount to Unstake</label>
                <input
                  type="number"
                  placeholder="e.g. 1000"
                  value={unstakeForm.ltg_amount}
                  onChange={(e) => setUnstakeForm({ ...unstakeForm, ltg_amount: e.target.value })}
                  className="input-dark"
                />
                {unstakeForm.ltg_amount && (
                  <p className="text-xs text-green-400 mt-1">
                    = ${(parseFloat(unstakeForm.ltg_amount) / 1000).toFixed(4)} USDT will be credited
                  </p>
                )}
              </div>
              <div>
                <label className="text-muted-foreground text-sm">Reason</label>
                <input
                  type="text"
                  placeholder="e.g. User request, Refund"
                  value={unstakeForm.reason}
                  onChange={(e) => setUnstakeForm({ ...unstakeForm, reason: e.target.value })}
                  className="input-dark"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <button 
                  onClick={() => setUnstakeForm({...unstakeForm, ltg_amount: "1000"})}
                  className="px-3 py-2 bg-purple-600 rounded-lg text-sm"
                >
                  1000 LTG
                </button>
                <button 
                  onClick={() => setUnstakeForm({...unstakeForm, ltg_amount: "5000"})}
                  className="px-3 py-2 bg-purple-600 rounded-lg text-sm"
                >
                  5000 LTG
                </button>
                <button 
                  onClick={() => setUnstakeForm({...unstakeForm, ltg_amount: "10000"})}
                  className="px-3 py-2 bg-purple-600 rounded-lg text-sm"
                >
                  10000 LTG
                </button>
                <button 
                  onClick={() => setUnstakeForm({...unstakeForm, ltg_amount: String(unstakeUser.staked_amount || 0)})}
                  className="px-3 py-2 bg-red-600 rounded-lg text-sm"
                >
                  All ({unstakeUser.staked_amount?.toFixed(0) || 0})
                </button>
              </div>
              <button 
                onClick={handleUnstake} 
                disabled={unstaking || !unstakeForm.ltg_amount}
                className="w-full bg-gradient-to-r from-orange-600 to-red-600 py-3 rounded-full disabled:opacity-50 font-bold"
              >
                {unstaking ? "Processing..." : "🔓 Unstake & Convert to USDT"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Login Logs Modal */}
      {logsUser && (
        <>
          <div className="modal-overlay" onClick={() => setLogsUser(null)}></div>
          <div className="modal-content">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-manrope font-bold text-xl">📋 Login History</h2>
              <button onClick={() => setLogsUser(null)}><X size={24} /></button>
            </div>
            
            <div className="bg-black/30 rounded-lg p-3 mb-4">
              <p className="font-bold">{logsUser.full_name}</p>
              <p className="text-muted-foreground text-sm">{logsUser.phone}</p>
            </div>

            <div className="max-h-80 overflow-y-auto space-y-2">
              {loginLogs.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No login history found</p>
              ) : (
                loginLogs.map((log, idx) => (
                  <div key={idx} className="bg-black/20 rounded-lg p-3 border border-border">
                    <div className="flex justify-between items-center">
                      <span className="text-green-400 text-sm">✅ Login</span>
                      <span className="text-muted-foreground text-xs">
                        {new Date(log.login_time).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    <p className="text-white text-sm mt-1">
                      {new Date(log.login_time).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </p>
                  </div>
                ))
              )}
            </div>

            <p className="text-muted-foreground text-xs mt-4 text-center">
              Showing last {loginLogs.length} logins
            </p>
          </div>
        </>
      )}

      {/* Referrals Modal */}
      {viewingReferrals && (
        <>
          <div className="modal-overlay" onClick={() => setViewingReferrals(null)}></div>
          <div className="modal-content" style={{ maxWidth: '800px', width: '95%' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-manrope font-bold text-xl">👥 Referrals of {viewingReferrals.full_name}</h2>
              <button onClick={() => setViewingReferrals(null)}><X size={24} /></button>
            </div>
            
            <div className="bg-black/30 rounded-lg p-3 mb-4">
              <p className="font-bold">{viewingReferrals.full_name}</p>
              <p className="text-muted-foreground text-sm">Referral Code: <span className="text-primary">{viewingReferrals.referral_code}</span></p>
              <p className="text-muted-foreground text-sm">Total Referrals: <span className="text-green-400">{referralsList.length}</span></p>
            </div>

            {loadingReferrals ? (
              <p className="text-center py-4">Loading...</p>
            ) : referralsList.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No referrals found</p>
            ) : (
              <div className="max-h-96 overflow-y-auto space-y-2">
                {referralsList.map((ref, idx) => (
                  <div key={idx} className="bg-black/20 rounded-lg p-3 border border-border">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-white">{ref.full_name}</p>
                        <p className="text-sm text-muted-foreground">📧 {ref.email || 'N/A'}</p>
                        <p className="text-sm text-muted-foreground">📱 {ref.phone}</p>
                        <p className="text-sm text-muted-foreground">🌍 {ref.country || 'N/A'}</p>
                        <p className="text-sm text-yellow-400">🔑 PIN: {ref.pin}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-yellow-500 text-sm">USDT: ${(ref.usdt_balance || 0).toFixed(4)}</p>
                        <p className="text-green-500 text-sm">Staked: {(ref.staked_amount || 0).toFixed(0)} LTG</p>
                        <p className={`text-xs ${ref.is_blocked ? 'text-red-500' : 'text-green-500'}`}>
                          {ref.is_blocked ? '🚫 Blocked' : '✅ Active'}
                        </p>
                        <p className="text-muted-foreground text-xs mt-1">
                          {new Date(ref.created_at).toLocaleDateString('en-IN')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* View as User Modal */}
      {viewingAsUser && (
        <>
          <div className="modal-overlay" onClick={() => setViewingAsUser(null)}></div>
          <div className="modal-content max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-card py-2 z-10">
              <h2 className="font-manrope font-bold text-xl">👁️ Viewing: {viewingAsUser.full_name}</h2>
              <button onClick={() => setViewingAsUser(null)}><X size={24} /></button>
            </div>
            
            {loadingUserView ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw size={24} className="animate-spin text-primary" />
                <span className="ml-2">Loading user data...</span>
              </div>
            ) : userViewData ? (
              <div className="space-y-4">
                {/* User Info */}
                <div className="bg-gradient-to-r from-primary/20 to-yellow-500/20 rounded-xl p-4">
                  <h3 className="font-bold text-lg mb-3">👤 User Info</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><span className="text-muted-foreground">Name:</span> {userViewData.user.full_name}</p>
                    <p><span className="text-muted-foreground">Email:</span> {userViewData.user.email}</p>
                    <p><span className="text-muted-foreground">Phone:</span> {userViewData.user.phone}</p>
                    <p><span className="text-muted-foreground">Country:</span> {userViewData.user.country}</p>
                    <p><span className="text-muted-foreground">Referral Code:</span> <span className="text-primary">{userViewData.user.referral_code}</span></p>
                    <p><span className="text-muted-foreground">Referred By:</span> {userViewData.user.referred_by || 'None'}</p>
                    <p><span className="text-muted-foreground">Status:</span> <span className={userViewData.user.is_blocked ? 'text-red-500' : 'text-green-500'}>{userViewData.user.is_blocked ? '🚫 Blocked' : '✅ Active'}</span></p>
                    <p><span className="text-muted-foreground">Joined:</span> {userViewData.user.created_at ? new Date(userViewData.user.created_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>

                {/* Balances */}
                <div className="bg-black/30 rounded-xl p-4">
                  <h3 className="font-bold text-lg mb-3">💰 Balances</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-yellow-500/20 rounded-lg p-3 text-center">
                      <p className="text-muted-foreground text-xs">USDT Balance</p>
                      <p className="text-yellow-500 font-bold text-lg">${userViewData.user.usdt_balance?.toFixed(4)}</p>
                    </div>
                    <div className="bg-green-500/20 rounded-lg p-3 text-center">
                      <p className="text-muted-foreground text-xs">Deposited USDT</p>
                      <p className="text-green-500 font-bold text-lg">${userViewData.user.deposited_usdt?.toFixed(4)}</p>
                    </div>
                    <div className="bg-blue-500/20 rounded-lg p-3 text-center">
                      <p className="text-muted-foreground text-xs">LTG Balance</p>
                      <p className="text-blue-500 font-bold text-lg">{userViewData.user.ltg_balance?.toFixed(0)}</p>
                    </div>
                    <div className="bg-purple-500/20 rounded-lg p-3 text-center">
                      <p className="text-muted-foreground text-xs">Staked LTG</p>
                      <p className="text-purple-500 font-bold text-lg">{userViewData.user.staked_amount?.toFixed(0)}</p>
                    </div>
                    <div className="bg-cyan-500/20 rounded-lg p-3 text-center">
                      <p className="text-muted-foreground text-xs">Staking Reward</p>
                      <p className="text-cyan-500 font-bold text-lg">${(userViewData.user.staked_reward / 1000)?.toFixed(4)}</p>
                    </div>
                    <div className="bg-orange-500/20 rounded-lg p-3 text-center">
                      <p className="text-muted-foreground text-xs">Sellable LTG</p>
                      <p className="text-orange-500 font-bold text-lg">{userViewData.user.sellable_ltg?.toFixed(0)}</p>
                    </div>
                    <div className="bg-pink-500/20 rounded-lg p-3 text-center">
                      <p className="text-muted-foreground text-xs">Referral Earnings</p>
                      <p className="text-pink-500 font-bold text-lg">{userViewData.user.referral_earnings?.toFixed(0)} LTG</p>
                    </div>
                    <div className="bg-indigo-500/20 rounded-lg p-3 text-center">
                      <p className="text-muted-foreground text-xs">Referrals Count</p>
                      <p className="text-indigo-500 font-bold text-lg">{userViewData.user.referrals_count}</p>
                    </div>
                  </div>
                </div>

                {/* Wallet Address */}
                <div className="bg-black/30 rounded-xl p-4">
                  <h3 className="font-bold text-lg mb-2">🔐 Wallet Address</h3>
                  <p className="text-xs text-muted-foreground break-all bg-black/30 p-2 rounded">{userViewData.user.wallet_address}</p>
                </div>

                {/* Transactions */}
                <div className="bg-black/30 rounded-xl p-4">
                  <h3 className="font-bold text-lg mb-3">📜 Transaction History ({userViewData.transactions?.length || 0})</h3>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {userViewData.transactions?.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No transactions</p>
                    ) : (
                      userViewData.transactions?.slice(0, 20).map((tx, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-black/20 rounded p-2 text-sm">
                          <div>
                            <span className={`font-bold ${tx.type === 'DEPOSIT' ? 'text-green-500' : tx.type === 'WITHDRAW' ? 'text-red-500' : 'text-blue-500'}`}>
                              {tx.type}
                            </span>
                            <span className="text-muted-foreground ml-2">{tx.status}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-yellow-500">{tx.amount} {tx.currency || 'LTG'}</p>
                            <p className="text-xs text-muted-foreground">{tx.created_at ? new Date(tx.created_at).toLocaleString() : 'N/A'}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Login Logs */}
                <div className="bg-black/30 rounded-xl p-4">
                  <h3 className="font-bold text-lg mb-3">🔑 Login History ({userViewData.login_logs?.length || 0})</h3>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {userViewData.login_logs?.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No login logs</p>
                    ) : (
                      userViewData.login_logs?.map((log, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-black/20 rounded p-2 text-sm">
                          <span className="text-cyan-400">IP: {log.ip || 'N/A'}</span>
                          <span className="text-muted-foreground">{log.login_time ? new Date(log.login_time).toLocaleString() : log.timestamp || 'N/A'}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Referrals */}
                <div className="bg-black/30 rounded-xl p-4">
                  <h3 className="font-bold text-lg mb-3">👥 Referrals ({userViewData.referrals?.length || 0})</h3>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {userViewData.referrals?.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No referrals</p>
                    ) : (
                      userViewData.referrals?.map((ref, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-black/20 rounded p-2 text-sm">
                          <div>
                            <p className="font-bold">{ref.full_name}</p>
                            <p className="text-muted-foreground text-xs">{ref.email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-green-500">${(ref.deposited_usdt || 0).toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">{ref.created_at ? new Date(ref.created_at).toLocaleDateString() : 'N/A'}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Stakes */}
                <div className="bg-black/30 rounded-xl p-4">
                  <h3 className="font-bold text-lg mb-3">🏦 Stakes ({userViewData.stakes?.length || 0})</h3>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {userViewData.stakes?.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No stakes</p>
                    ) : (
                      userViewData.stakes?.map((stake, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-black/20 rounded p-2 text-sm">
                          <div>
                            <p className="text-purple-400 font-bold">{stake.amount} LTG</p>
                            <p className="text-muted-foreground text-xs">{stake.status || 'Active'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-cyan-400">${(stake.reward_earned || 0).toFixed(4)}</p>
                            <p className="text-xs text-muted-foreground">{stake.created_at ? new Date(stake.created_at).toLocaleDateString() : 'N/A'}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-red-500 py-4">Failed to load user data</p>
            )}
          </div>
        </>
      )}

      {/* Investigation Tab */}
      {tab === "investigate" && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 border border-purple-500/30 rounded-xl p-5">
            <h3 className="text-xl font-bold text-purple-400 mb-3 flex items-center gap-2">
              <Search size={24} /> Investigation Center
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              Kuch bhi search karo - system automatically samjhega aur relevant data dikhayega
            </p>
            
            {/* Search Box */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={investigateQuery}
                onChange={(e) => setInvestigateQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && investigateQuery.trim()) {
                    handleInvestigate();
                  }
                }}
                placeholder="Type your query... (e.g., 'same ip 5+', 'pakistan blocked', 'top referrers')"
                className="flex-1 bg-black/50 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder:text-muted-foreground focus:outline-none focus:border-purple-500"
                data-testid="investigate-search-input"
              />
              <button
                onClick={() => {
                  if (investigateQuery.trim()) {
                    handleInvestigate();
                  }
                }}
                disabled={investigating || !investigateQuery.trim()}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg font-bold flex items-center gap-2"
                data-testid="investigate-search-btn"
              >
                {investigating ? <RefreshCw size={18} className="animate-spin" /> : <Search size={18} />}
                Search
              </button>
            </div>
            
            {/* Example Queries */}
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-muted-foreground">Examples:</span>
              {[
                "same ip 3+",
                "top 10 referrers",
                "blocked pakistan",
                "last 7 days signups",
                "inactive referrals",
                "gmail users",
                "not activated india"
              ].map((example) => (
                <button
                  key={example}
                  onClick={() => {
                    setInvestigateQuery(example);
                  }}
                  className="text-xs px-2 py-1 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-full text-purple-300"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>

          {/* Results */}
          {investigateResults && (
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h4 className="font-bold text-lg">{investigateResults.description}</h4>
                  <p className="text-sm text-muted-foreground">
                    Type: <span className="text-primary">{investigateResults.type}</span> | 
                    Found: <span className="text-green-400">{investigateResults.count}</span> results
                  </p>
                </div>
                <button
                  onClick={() => setInvestigateResults(null)}
                  className="p-2 hover:bg-secondary rounded-lg"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Suggestions if no results */}
              {investigateResults.count === 0 && investigateResults.suggestions?.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
                  <p className="text-yellow-400 text-sm font-bold mb-2">No results found. Try these:</p>
                  <div className="flex flex-wrap gap-2">
                    {investigateResults.suggestions.map((s, i) => (
                      <span key={i} className="text-xs text-yellow-300">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Results Table */}
              {investigateResults.count > 0 && (
                <div className="max-h-96 overflow-y-auto">
                  {/* IP Groups (for same_ip_accounts type) */}
                  {investigateResults.type === "same_ip_accounts" && (
                    <div className="space-y-3">
                      {investigateResults.data.map((ipGroup, idx) => (
                        <div key={idx} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-mono text-red-400">{ipGroup.ip}</span>
                            <span className="text-sm bg-red-500/20 px-2 py-1 rounded text-red-300">
                              {ipGroup.account_count} accounts
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {ipGroup.users.map((u, i) => (
                              <div key={i} className="text-xs bg-black/30 p-2 rounded">
                                <p className="text-white">{u.full_name}</p>
                                <p className="text-muted-foreground">{u.phone}</p>
                                {u.is_blocked && <span className="text-red-400">🚫 Blocked</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* User List (for other types) */}
                  {investigateResults.type !== "same_ip_accounts" && (
                    <table className="w-full text-sm">
                      <thead className="bg-secondary/50">
                        <tr>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Phone</th>
                          <th className="text-left p-2">Country</th>
                          <th className="text-left p-2">Status</th>
                          {investigateResults.type === "top_referrers" && <th className="text-left p-2">Referrals</th>}
                          {investigateResults.type === "inactive_referrals" && <th className="text-left p-2">Inactive</th>}
                          {investigateResults.type === "ip_country_mismatch" && <th className="text-left p-2">Suspicious</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {investigateResults.data.map((user, idx) => (
                          <tr key={idx} className="border-b border-border hover:bg-secondary/20">
                            <td className="p-2">
                              <p className="font-medium">{user.full_name}</p>
                              <p className="text-xs text-muted-foreground">{user.email}</p>
                            </td>
                            <td className="p-2 font-mono text-xs">{user.phone}</td>
                            <td className="p-2">{user.country}</td>
                            <td className="p-2">
                              {user.is_blocked && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">Blocked</span>}
                              {!user.is_blocked && user.is_activated && <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Active</span>}
                              {!user.is_blocked && !user.is_activated && <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">Not Active</span>}
                            </td>
                            {investigateResults.type === "top_referrers" && (
                              <td className="p-2 text-primary font-bold">{user.referrals_count}</td>
                            )}
                            {investigateResults.type === "inactive_referrals" && (
                              <td className="p-2 text-red-400 font-bold">{user.inactive_count}</td>
                            )}
                            {investigateResults.type === "ip_country_mismatch" && (
                              <td className="p-2 text-orange-400 text-xs">
                                {user.suspicious_logins?.join(", ") || "N/A"}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {tab === "transactions" && (
        <div className="space-y-3">
          {loadingTab === "transactions" ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={24} className="animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading transactions...</span>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <p className="text-yellow-500">{t("pending")}: {pendingTx.length}</p>
                <button onClick={refreshCurrentTab} className="p-2 bg-card rounded-lg hover:bg-primary/20">
                  <RefreshCw size={16} />
                </button>
              </div>
              {pendingTx.map(tx => (
                <div key={tx.id} className="admin-user-card">
                  <p className="font-bold">{tx.type}: {tx.amount} {tx.currency}</p>
                  <p className="text-muted-foreground text-sm">{tx.user_name} • {tx.user_email}</p>
                  {tx.tx_hash && (
                    <div className="flex items-center gap-2 mt-2 bg-black/30 p-2 rounded-lg">
                      <span className="text-xs text-yellow-400">TX Hash:</span>
                      <p className="text-green-400 text-xs truncate flex-1 font-mono">{tx.tx_hash}</p>
                      <button onClick={() => copy(tx.tx_hash)} className="p-1 bg-card rounded hover:bg-primary/20"><Copy size={14} /></button>
                    </div>
                  )}
                  {tx.address && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-muted-foreground">Address:</span>
                      <p className="text-primary text-xs truncate flex-1">{tx.address}</p>
                      <button onClick={() => copy(tx.address)} className="p-1 bg-card rounded"><Copy size={14} /></button>
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <button onClick={() => handleTx(tx.id, "approve")} className="flex-1 py-2 bg-green-600 rounded-lg font-bold">{t("approve")}</button>
                <button onClick={() => handleTx(tx.id, "reject")} className="flex-1 py-2 bg-red-600 rounded-lg font-bold">{t("reject")}</button>
              </div>
            </div>
          ))}
          {pendingTx.length === 0 && <p className="text-muted-foreground text-center py-8">No pending transactions</p>}
            </>
          )}
        </div>
      )}

      {/* Support Chat Tab */}
      {tab === "support" && (
        <div className="space-y-4">
          {loadingTab === "support" ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={24} className="animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading support chats...</span>
            </div>
          ) : (
            <>
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-lg">💬 Support Chats</h3>
            <div className="flex items-center gap-2">
              <span className="bg-primary text-black px-3 py-1 rounded-full text-sm font-bold">
                {supportChats.filter(c => c.unread_admin > 0).length} Unread
              </span>
              <button onClick={refreshCurrentTab} className="p-2 bg-card rounded-lg hover:bg-primary/20">
                <RefreshCw size={16} />
              </button>
            </div>
          </div>

          {!selectedChat ? (
            // Chat List
            <div className="space-y-2">
              {supportChats.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No support chats yet</p>
              ) : (
                supportChats.map(chat => (
                  <div 
                    key={chat.id} 
                    onClick={() => openChat(chat)}
                    className={`admin-user-card cursor-pointer hover:border-primary/50 ${chat.unread_admin > 0 ? 'border-primary' : ''}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold flex items-center gap-2">
                          {chat.user_name}
                          {chat.unread_admin > 0 && (
                            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                              {chat.unread_admin} new
                            </span>
                          )}
                        </p>
                        <p className="text-muted-foreground text-sm">{chat.user_phone}</p>
                        <p className="text-sm text-gray-400 mt-1 truncate">{chat.last_message || "No messages"}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${chat.status === 'open' ? 'bg-green-600' : 'bg-gray-600'}`}>
                          {chat.status}
                        </span>
                        <button 
                          onClick={(e) => { e.stopPropagation(); deleteChat(chat.id); }}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            // Chat Detail View
            <div className="bg-card rounded-xl border border-border">
              {/* Chat Header */}
              <div className="p-4 border-b border-border flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <button onClick={() => { setSelectedChat(null); setChatMessages([]); }} className="p-2">
                    <ChevronLeft size={20} />
                  </button>
                  <div>
                    <p className="font-bold">{selectedChat.user_name}</p>
                    <p className="text-xs text-muted-foreground">{selectedChat.user_phone}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {selectedChat.status === 'open' && (
                    <button onClick={() => closeChat(selectedChat.id)} className="px-3 py-1 bg-yellow-600 rounded-full text-sm">
                      Close
                    </button>
                  )}
                  <button onClick={() => deleteChat(selectedChat.id)} className="px-3 py-1 bg-red-600 rounded-full text-sm">
                    Delete
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="h-64 overflow-y-auto p-4 space-y-3">
                {chatMessages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl p-3 ${
                      msg.sender === "admin" 
                        ? "bg-primary text-primary-foreground rounded-br-sm" 
                        : "bg-black/30 border border-border rounded-bl-sm"
                    }`}>
                      {msg.message_type === "text" && <p className="text-sm">{msg.message}</p>}
                      {msg.message_type === "image" && msg.file_url && (
                        <img src={msg.file_url} alt="Shared" className="max-w-full rounded-lg" />
                      )}
                      {msg.message_type === "audio" && msg.file_url && (
                        <audio controls className="max-w-full"><source src={msg.file_url} type="audio/webm" /></audio>
                      )}
                      <p className={`text-xs mt-1 ${msg.sender === "admin" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        {new Date(msg.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              {selectedChat.status === 'open' && (
                <div className="p-4 border-t border-border flex gap-2">
                  <input
                    type="text"
                    value={adminReply}
                    onChange={(e) => setAdminReply(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && sendAdminReply()}
                    placeholder="Type reply..."
                    className="flex-1 input-dark rounded-full px-4"
                  />
                  <button 
                    onClick={sendAdminReply}
                    disabled={!adminReply.trim() || sendingReply}
                    className="px-4 py-2 bg-primary rounded-full disabled:opacity-50"
                  >
                    {sendingReply ? <RefreshCw size={18} className="animate-spin" /> : <Send size={18} />}
                  </button>
                </div>
              )}
            </div>
          )}
            </>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {tab === "settings" && (
        <div className="space-y-4">
          {loadingTab === "settings" ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={24} className="animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading settings...</span>
            </div>
          ) : (
            <>
          {/* Welcome Email Toggle */}
          <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📧</span>
                <div>
                  <h3 className="font-bold text-lg">Welcome Email</h3>
                  <p className="text-muted-foreground text-sm">नया account बनने पर Welcome mail भेजें</p>
                </div>
              </div>
              <button
                onClick={toggleWelcomeEmail}
                disabled={togglingWelcomeEmail}
                className={`relative w-14 h-7 rounded-full transition-all duration-300 ${welcomeEmailEnabled ? 'bg-green-500' : 'bg-gray-600'}`}
              >
                <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all duration-300 ${welcomeEmailEnabled ? 'left-8' : 'left-1'}`}></span>
              </button>
            </div>
            <div className={`mt-3 p-2 rounded-lg text-center text-sm font-bold ${welcomeEmailEnabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {welcomeEmailEnabled ? '✅ ON - Welcome mail जा रहा है' : '❌ OFF - Welcome mail बंद है'}
            </div>
          </div>

          {/* Deposit Wallet Address - Fund Receiving */}
          <div className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 border border-green-500/30 rounded-xl p-4">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <span className="text-2xl">💳</span> Deposit Wallet Address
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              जिस address पर users USDT/BNB भेजते हैं (यही सबको दिखेगा)
            </p>
            
            {/* Current Address */}
            <div className="bg-black/30 rounded-lg p-3 mb-4">
              <p className="text-xs text-muted-foreground mb-1">Current Address:</p>
              <p className="text-green-400 font-mono text-sm break-all">{depositWalletAddress || "Not set"}</p>
            </div>
            
            {/* Update Address */}
            <div className="flex gap-2">
              <input 
                type="text"
                placeholder="New wallet address (0x...)"
                value={newDepositWallet}
                onChange={(e) => setNewDepositWallet(e.target.value)}
                className="input-dark flex-1 text-sm"
              />
              <button 
                onClick={updateDepositWallet}
                disabled={savingDepositWallet || !newDepositWallet.trim() || newDepositWallet === depositWalletAddress}
                className="px-4 py-2 bg-green-600 rounded-lg font-bold disabled:opacity-50"
              >
                {savingDepositWallet ? "..." : "✓ Update"}
              </button>
            </div>
            
            <p className="text-yellow-400 text-xs mt-3">
              ⚠️ Update करते ही सभी users को नया address दिखेगा
            </p>
          </div>

          {/* Per-Country Activation Fees */}
          <div className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 border border-indigo-500/30 rounded-xl p-4" data-testid="activation-fees-card">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <span className="text-2xl">💰</span> Account Activation Fees (per country)
            </h3>
            <p className="text-muted-foreground text-sm mb-3">
              Default fee: <b className="text-white">${activationFees.default_fee_usd}</b> — worldwide fallback. Override any country below.
            </p>

            {/* Add / update fee */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
              <select
                value={afCountry}
                onChange={(e) => setAfCountry(e.target.value)}
                className="input-dark text-sm md:col-span-1"
                data-testid="af-country-select"
              >
                <option value="">-- Select country --</option>
                <option value="__all_except_india">🌐 All Countries (except India)</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Fee in USD (e.g. 30)"
                value={afFee}
                onChange={(e) => setAfFee(e.target.value)}
                className="input-dark text-sm md:col-span-1"
                data-testid="af-fee-input"
              />
              <input
                type="text"
                placeholder="Note (optional)"
                value={afNote}
                onChange={(e) => setAfNote(e.target.value)}
                className="input-dark text-sm md:col-span-1"
                data-testid="af-note-input"
              />
              <button
                onClick={saveActivationFee}
                disabled={afSaving}
                className="btn-primary text-sm py-2 disabled:opacity-50"
                data-testid="af-save-btn"
              >
                {afSaving ? "Saving..." : "Save"}
              </button>
            </div>

            {/* Existing overrides list */}
            {Object.keys(activationFees.per_country || {}).length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No country overrides yet. All new signups pay the default ${activationFees.default_fee_usd}.</p>
            ) : (
              <div className="space-y-2" data-testid="af-list">
                {Object.entries(activationFees.per_country).map(([country, info]) => (
                  <div key={country} className="flex items-center justify-between bg-black/30 border border-white/5 rounded-lg px-3 py-2">
                    <div>
                      <span className="text-white font-semibold capitalize">{country}</span>
                      <span className="ml-3 text-emerald-400 font-mono">${info.fee_usd}</span>
                      {info.note && <span className="ml-3 text-xs text-muted-foreground italic">&ldquo;{info.note}&rdquo;</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setAfCountry(country); setAfFee(String(info.fee_usd)); setAfNote(info.note || ""); }}
                        className="text-xs text-indigo-300 hover:text-indigo-200 underline"
                        data-testid={`af-edit-${country}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteActivationFee(country)}
                        className="text-xs text-red-400 hover:text-red-300"
                        data-testid={`af-delete-${country}`}
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-yellow-300/80 text-[11px] mt-3">
              💡 Changes apply to NEW registrations only. Existing users&apos; activation_required field is not touched.
            </p>
          </div>

          {/* Bulk Deactivate Country — anti-fraud tool */}
          <div className="bg-gradient-to-r from-red-900/50 to-orange-900/40 border border-red-500/40 rounded-xl p-4" data-testid="bulk-deactivate-card">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <span className="text-2xl">🛑</span> Bulk Deactivate Accounts by Country
            </h3>
            <p className="text-muted-foreground text-sm mb-3">
              Fraud control tool. Selects ALL non-admin users of a country and marks them <b className="text-red-300">is_activated = false</b>. They will have to make a fresh deposit (of their country&apos;s activation fee) to reactivate.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
              <select
                value={bdCountry}
                onChange={(e) => { setBdCountry(e.target.value); setBdPreview(null); }}
                className="input-dark text-sm md:col-span-1"
                data-testid="bd-country-select"
              >
                <option value="">-- Select country --</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Reason (audit log)"
                value={bdReason}
                onChange={(e) => setBdReason(e.target.value)}
                className="input-dark text-sm md:col-span-2"
                data-testid="bd-reason-input"
              />
              <button
                onClick={previewBulkDeactivate}
                disabled={bdBusy || !bdCountry}
                className="btn-primary text-sm py-2 disabled:opacity-50"
                data-testid="bd-preview-btn"
              >
                {bdBusy ? "..." : "Preview"}
              </button>
            </div>

            {bdPreview && (
              <div className="bg-black/40 border border-red-500/30 rounded-lg p-3 mb-2 flex items-center justify-between" data-testid="bd-preview-box">
                <div className="text-sm">
                  <span className="text-red-300 font-bold text-lg">{bdPreview.matched.toLocaleString()}</span>
                  {" "}<span className="text-white">{bdCountry}</span> accounts will be deactivated.
                </div>
                <button
                  onClick={confirmBulkDeactivate}
                  disabled={bdBusy || bdPreview.matched === 0}
                  className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-bold disabled:opacity-50"
                  data-testid="bd-confirm-btn"
                >
                  {bdBusy ? "..." : "Confirm & Deactivate"}
                </button>
              </div>
            )}

            <p className="text-yellow-300/80 text-[11px] mt-2">
              ⚠️ Irreversible in bulk. Users&apos; wallets, balances, referrals, and total_deposited_usdt stay untouched — only <b>is_activated</b> flips to false. To reactivate they must deposit their country&apos;s fee amount again.
            </p>
          </div>

          {/* Blocked Names - Fraud Prevention */}
          <div className="bg-gradient-to-r from-red-900/50 to-orange-900/50 border border-red-500/30 rounded-xl p-4">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <span className="text-2xl">🚫</span> Blocked Names
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              इन नामों से कोई account नहीं बना सकता (Fraud prevention)
            </p>
            
            {/* Add new blocked name */}
            <div className="flex gap-2 mb-4">
              <input 
                type="text"
                placeholder="Block करने का नाम..."
                value={newBlockedName}
                onChange={(e) => setNewBlockedName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addBlockedName()}
                className="input-dark flex-1"
              />
              <button 
                onClick={addBlockedName}
                disabled={addingBlockedName || !newBlockedName.trim()}
                className="px-4 py-2 bg-red-600 rounded-lg font-bold disabled:opacity-50"
              >
                {addingBlockedName ? "..." : "🚫 Block"}
              </button>
            </div>

            {/* List of blocked names */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {blockedNames.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">कोई blocked name नहीं</p>
              ) : (
                blockedNames.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
                    <div>
                      <span className="font-bold text-red-400">{item.name}</span>
                      <span className="text-muted-foreground text-xs ml-2">({item.reason})</span>
                    </div>
                    <button
                      onClick={() => removeBlockedName(item.name)}
                      className="px-3 py-1 bg-green-600 rounded-full text-xs"
                    >
                      Unblock
                    </button>
                  </div>
                ))
              )}
            </div>
            
            <p className="text-yellow-400 text-xs mt-3">
              ⚠️ अगर कोई user का नाम "yaboku" block किया तो "yaboku", "yaboku123", "myyaboku" सब block होंगे
            </p>
          </div>

          {/* Referral Deposit Bonus Settings */}
          <div className="bg-gradient-to-r from-cyan-900/50 to-blue-900/50 border border-cyan-500/30 rounded-xl p-4">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <span className="text-2xl">💰</span> Referral Deposit Bonus
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              जब user USDT deposit करके LTG buy करके stake करे, तो referrer को bonus मिलेगा
            </p>
            <div className="flex items-center gap-3 mb-3">
              <input 
                type="number" 
                value={referralBonusPercent}
                onChange={(e) => setReferralBonusPercent(e.target.value)}
                min="0"
                max="100"
                className="input-dark w-24 text-center text-xl font-bold"
              />
              <span className="text-2xl font-bold text-primary">%</span>
              <span className="text-muted-foreground text-sm">of Deposited USDT</span>
            </div>
            <div className="flex gap-2 mb-4">
              {[5, 10, 15, 20].map(p => (
                <button 
                  key={p}
                  onClick={() => setReferralBonusPercent(p)}
                  className={`px-3 py-1 rounded-full text-sm ${referralBonusPercent == p ? 'bg-primary text-black' : 'bg-card'}`}
                >
                  {p}%
                </button>
              ))}
            </div>
            <div className="bg-black/30 rounded-lg p-3 mb-4 text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-400">⚠️ Pending Bonuses: {pendingBonusCount}</p>
                  <p className="text-muted-foreground text-xs mt-1">Users जिन्होंने deposit किया लेकिन अभी stake नहीं किया</p>
                </div>
                {pendingBonusCount > 0 && (
                  <button 
                    onClick={() => setShowPendingList(!showPendingList)}
                    className="text-primary text-sm underline"
                  >
                    {showPendingList ? "Hide List" : "View Details"}
                  </button>
                )}
              </div>
              
              {/* Pending List */}
              {showPendingList && pendingBonusList.length > 0 && (
                <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                  {pendingBonusList.map((item, idx) => (
                    <div key={idx} className="bg-black/40 rounded-lg p-3 border border-yellow-500/20">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-white">{item.user_name}</p>
                          <p className="text-xs text-muted-foreground">{item.user_phone}</p>
                          <p className="text-xs text-cyan-400 mt-1">
                            Referrer: {item.referrer_name}
                          </p>
                          <div className="mt-2 text-xs">
                            <span className="text-green-400">Deposited: ${item.total_deposited}</span>
                            <span className="mx-2 text-muted-foreground">|</span>
                            <span className="text-yellow-400">Pending: ${item.pending_amount}</span>
                            <span className="mx-2 text-muted-foreground">|</span>
                            <span className="text-primary">Bonus: ${item.potential_bonus?.toFixed(2)}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => clearPendingBonus(item.user_id, item.user_name)}
                          className="bg-red-600/80 hover:bg-red-600 text-white text-xs px-3 py-1.5 rounded-full"
                          title="Clear this pending bonus"
                        >
                          ❌ Clear
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button 
              onClick={saveReferralBonusPercent}
              disabled={savingReferralBonus}
              className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 py-3 rounded-full font-bold disabled:opacity-50"
            >
              {savingReferralBonus ? "Saving..." : "💾 Save Bonus Percentage"}
            </button>
          </div>

          {/* Change PIN */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-bold text-lg mb-4">{t("changePin")}</h3>
            <div className="mb-3">
              <label className="text-muted-foreground text-sm">{t("currentPin")}</label>
              <input type="text" value={adminPin} readOnly className="input-dark bg-black/50" />
            </div>
            <input type="password" placeholder={t("currentPin")} value={oldPin} onChange={(e) => setOldPin(e.target.value)} className="input-dark mb-3" />
            <input type="password" placeholder="New 8-digit PIN" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} maxLength={8} className="input-dark mb-3" />
            <input type="password" placeholder="Confirm PIN" value={confirmNewPin} onChange={(e) => setConfirmNewPin(e.target.value.replace(/\D/g, ""))} maxLength={8} className="input-dark mb-4" />
            <button onClick={changePin} className="w-full btn-gold py-3 rounded-full">{t("save")}</button>
          </div>

          {/* Bulk Email */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-bold text-lg mb-4">📧 Bulk Email to All Users</h3>
            <input 
              type="text" 
              placeholder="Email Subject" 
              value={bulkEmailSubject} 
              onChange={(e) => setBulkEmailSubject(e.target.value)} 
              className="input-dark mb-3" 
            />
            <textarea 
              placeholder="Email Message (supports multiple lines)" 
              value={bulkEmailMessage} 
              onChange={(e) => setBulkEmailMessage(e.target.value)} 
              className="input-dark mb-4 min-h-[150px]"
              rows={6}
            />
            <button 
              onClick={sendBulkEmail} 
              disabled={sendingBulkEmail || !bulkEmailSubject || !bulkEmailMessage}
              className="w-full btn-gold py-3 rounded-full disabled:opacity-50"
            >
              {sendingBulkEmail ? "Sending..." : `Send to All Users (${users.length})`}
            </button>
          </div>

          {/* Active Announcement - Show if exists */}
          {activeAnnouncement && (
            <div className="bg-gradient-to-r from-red-900/50 to-orange-900/50 border border-red-500/30 rounded-xl p-4">
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">🔴</span> Active Announcement
                </h3>
                <span className="px-2 py-1 bg-green-500 text-black text-xs rounded-full font-bold animate-pulse">LIVE</span>
              </div>
              <div className="bg-black/30 rounded-lg p-3 mb-3">
                <p className="font-bold text-primary">{activeAnnouncement.title}</p>
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{activeAnnouncement.message}</p>
              </div>
              <button 
                onClick={stopAnnouncement}
                className="w-full bg-red-600 hover:bg-red-700 py-3 rounded-full font-bold flex items-center justify-center gap-2"
              >
                🛑 Stop Announcement
              </button>
            </div>
          )}

          {/* Send New Announcement */}
          <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-xl p-4">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <span className="text-2xl">📢</span> Send New Announcement
            </h3>
            <p className="text-muted-foreground text-sm mb-4">This will show a popup to all users when they open the website.</p>
            <input 
              type="text" 
              placeholder="Announcement Title" 
              value={announcementTitle} 
              onChange={(e) => setAnnouncementTitle(e.target.value)} 
              className="input-dark mb-3" 
            />
            <textarea 
              placeholder="Announcement Message (supports multiple lines)" 
              value={announcementMessage} 
              onChange={(e) => setAnnouncementMessage(e.target.value)} 
              className="input-dark mb-4 min-h-[120px]"
              rows={5}
            />
            <button 
              onClick={sendAnnouncement} 
              disabled={sendingAnnouncement || !announcementTitle || !announcementMessage}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 py-3 rounded-full font-bold disabled:opacity-50"
            >
              {sendingAnnouncement ? "Sending..." : "📢 Send Announcement"}
            </button>
          </div>
            </>
          )}
        </div>
      )}

      {/* Promo Codes Tab */}
      {tab === "promo" && (
        <div className="space-y-4">
          {/* Create Promo Code */}
          <div className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 border border-green-500/30 rounded-xl p-4">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <span className="text-2xl">🎁</span> Create Promo Code
            </h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input 
                type="text" 
                placeholder="Promo Code (e.g., BONUS100)" 
                value={promoForm.code}
                onChange={(e) => setPromoForm({...promoForm, code: e.target.value.toUpperCase()})}
                className="input-dark col-span-2"
              />
              <select 
                value={promoForm.bonus_type}
                onChange={(e) => setPromoForm({...promoForm, bonus_type: e.target.value})}
                className="input-dark"
              >
                <option value="ltg">LTG Bonus</option>
                <option value="usdt">USDT Bonus</option>
              </select>
              <input 
                type="number" 
                placeholder="Bonus Amount" 
                value={promoForm.bonus_amount}
                onChange={(e) => setPromoForm({...promoForm, bonus_amount: e.target.value})}
                className="input-dark"
              />
              <input 
                type="number" 
                placeholder="Max Uses (default: 100)" 
                value={promoForm.max_uses}
                onChange={(e) => setPromoForm({...promoForm, max_uses: e.target.value})}
                className="input-dark"
              />
              <input 
                type="datetime-local" 
                placeholder="Expires At (optional)" 
                value={promoForm.expires_at}
                onChange={(e) => setPromoForm({...promoForm, expires_at: e.target.value})}
                className="input-dark"
              />
            </div>
            <button 
              onClick={createPromoCode}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 py-3 rounded-full font-bold"
            >
              🎁 Create Promo Code
            </button>
          </div>

          {/* Active Promo Codes */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-bold text-lg mb-4">📋 Active Promo Codes ({promoCodes.length})</h3>
            {promoCodes.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No promo codes yet</p>
            ) : (
              <div className="space-y-3">
                {promoCodes.map(promo => (
                  <div key={promo.id} className={`p-3 rounded-xl border ${promo.is_active ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-lg text-primary">{promo.code}</p>
                        <p className="text-sm text-muted-foreground">
                          +{promo.bonus_amount} {promo.bonus_type.toUpperCase()} | 
                          Used: {promo.used_count}/{promo.max_uses}
                        </p>
                        {promo.expires_at && (
                          <p className="text-xs text-yellow-400">Expires: {new Date(promo.expires_at).toLocaleDateString()}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => togglePromoCode(promo.code)}
                          className={`px-3 py-1 rounded-full text-xs ${promo.is_active ? 'bg-yellow-600' : 'bg-green-600'}`}
                        >
                          {promo.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button 
                          onClick={() => deletePromoCode(promo.code)}
                          className="px-3 py-1 bg-red-600 rounded-full text-xs"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {tab === "notifications" && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-blue-900/50 to-indigo-900/50 border border-blue-500/30 rounded-xl p-4">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <span className="text-2xl">🔔</span> Send Notification
            </h3>
            <p className="text-muted-foreground text-sm mb-4">Send push notifications to users. Leave user empty to send to all.</p>
            
            <select 
              value={notifForm.user_id}
              onChange={(e) => setNotifForm({...notifForm, user_id: e.target.value})}
              className="input-dark mb-3"
            >
              <option value="">📢 All Users</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>👤 {u.full_name} ({u.phone})</option>
              ))}
            </select>

            <input 
              type="text" 
              placeholder="Notification Title" 
              value={notifForm.title}
              onChange={(e) => setNotifForm({...notifForm, title: e.target.value})}
              className="input-dark mb-3"
            />
            
            <textarea 
              placeholder="Notification Message" 
              value={notifForm.message}
              onChange={(e) => setNotifForm({...notifForm, message: e.target.value})}
              className="input-dark mb-3 min-h-[100px]"
              rows={4}
            />

            <select 
              value={notifForm.notification_type}
              onChange={(e) => setNotifForm({...notifForm, notification_type: e.target.value})}
              className="input-dark mb-4"
            >
              <option value="info">ℹ️ Info</option>
              <option value="success">✅ Success</option>
              <option value="warning">⚠️ Warning</option>
              <option value="promo">🎁 Promo</option>
            </select>

            <button 
              onClick={sendNotification}
              disabled={sendingNotif || !notifForm.title || !notifForm.message}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 py-3 rounded-full font-bold disabled:opacity-50"
            >
              {sendingNotif ? "Sending..." : `🔔 Send ${notifForm.user_id ? 'to User' : 'to All Users'}`}
            </button>

            {/* Clear All Notifications */}
            <div className="mt-4 pt-4 border-t border-border">
              <button 
                onClick={async () => {
                  if (!window.confirm("क्या आप सभी users की notifications clear करना चाहते हैं?")) return;
                  try {
                    const res = await adminApi.delete(`/admin/notifications/clear-all`);
                    toast.success(`✅ ${res.data.deleted_count} notifications cleared`);
                  } catch (e) {
                    toast.error("Failed to clear");
                  }
                }}
                className="w-full bg-red-600/80 hover:bg-red-600 py-2 rounded-full text-sm"
              >
                🗑️ Clear All Notifications (All Users)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Templates Tab */}
      {tab === "templates" && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-orange-900/50 to-amber-900/50 border border-orange-500/30 rounded-xl p-4">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <span className="text-2xl">📧</span> Email Templates
            </h3>
            <p className="text-muted-foreground text-sm mb-4">Customize email templates sent to users.</p>

            <div className="flex gap-2 mb-4 flex-wrap">
              {["welcome", "withdrawal_approved", "withdrawal_rejected", "promo"].map(template => (
                <button 
                  key={template}
                  onClick={() => loadTemplate(template)}
                  className={`px-3 py-2 rounded-lg text-sm ${selectedTemplate === template ? 'bg-primary text-black' : 'bg-card'}`}
                >
                  {template === "welcome" && "👋 Welcome"}
                  {template === "withdrawal_approved" && "✅ Withdrawal Approved"}
                  {template === "withdrawal_rejected" && "❌ Withdrawal Rejected"}
                  {template === "promo" && "🎁 Promo"}
                </button>
              ))}
            </div>

            <input 
              type="text" 
              placeholder="Email Subject" 
              value={templateForm.subject}
              onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})}
              className="input-dark mb-3"
            />
            
            <textarea 
              placeholder="Email Body (use {name}, {amount}, {code} as placeholders)" 
              value={templateForm.body}
              onChange={(e) => setTemplateForm({...templateForm, body: e.target.value})}
              className="input-dark mb-4 min-h-[150px]"
              rows={6}
            />

            <div className="bg-card/50 rounded-lg p-3 mb-4 text-sm">
              <p className="text-muted-foreground mb-1">Available placeholders:</p>
              <p className="text-primary">{"{name}"} - User's name | {"{amount}"} - Amount | {"{code}"} - Promo code</p>
            </div>

            <button 
              onClick={saveTemplate}
              disabled={savingTemplate || !templateForm.subject || !templateForm.body}
              className="w-full bg-gradient-to-r from-orange-600 to-amber-600 py-3 rounded-full font-bold disabled:opacity-50"
            >
              {savingTemplate ? "Saving..." : "💾 Save Template"}
            </button>
          </div>
        </div>
      )}

      {/* Reports Tab */}
      {tab === "reports" && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-bold text-lg mb-4">📊 Financial Report</h3>
            
            {/* Date Selection */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Single Day</label>
                <input
                  type="date"
                  value={reportDate}
                  onChange={(e) => { setReportDate(e.target.value); setReportStartDate(""); setReportEndDate(""); }}
                  className="input-dark text-sm"
                />
              </div>
              <div>
                <button
                  onClick={() => fetchFinancialReport(true)}
                  disabled={loadingReport}
                  className="w-full h-full bg-primary text-black rounded-xl font-bold disabled:opacity-50"
                >
                  {loadingReport ? "Loading..." : "📅 Get Report"}
                </button>
              </div>
            </div>

            <div className="text-center text-muted-foreground text-xs mb-2">— OR Date Range —</div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <input
                type="date"
                placeholder="Start"
                value={reportStartDate}
                onChange={(e) => { setReportStartDate(e.target.value); setReportDate(""); }}
                className="input-dark text-xs"
              />
              <input
                type="date"
                placeholder="End"
                value={reportEndDate}
                onChange={(e) => { setReportEndDate(e.target.value); setReportDate(""); }}
                className="input-dark text-xs"
              />
              <button
                onClick={() => fetchFinancialReport(false)}
                disabled={loadingReport || !reportStartDate || !reportEndDate}
                className="bg-purple-600 text-white rounded-xl text-xs font-bold disabled:opacity-50"
              >
                Get Range
              </button>
            </div>

            {/* Summary */}
            {financialReport && (
              <>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 text-center">
                    <p className="text-green-400 text-xs">Total Deposits</p>
                    <p className="text-green-400 font-bold text-lg">${financialReport.summary.total_deposits.toFixed(2)}</p>
                    <p className="text-green-400/70 text-xs">{financialReport.summary.deposit_count} txns</p>
                  </div>
                  <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 text-center">
                    <p className="text-red-400 text-xs">Total Withdrawals</p>
                    <p className="text-red-400 font-bold text-lg">${financialReport.summary.total_withdrawals.toFixed(2)}</p>
                    <p className="text-red-400/70 text-xs">{financialReport.summary.withdrawal_count} txns</p>
                  </div>
                  <div className={`${financialReport.summary.net_flow >= 0 ? 'bg-blue-500/20 border-blue-500/30' : 'bg-orange-500/20 border-orange-500/30'} border rounded-lg p-3 text-center`}>
                    <p className={`${financialReport.summary.net_flow >= 0 ? 'text-blue-400' : 'text-orange-400'} text-xs`}>Net Flow</p>
                    <p className={`${financialReport.summary.net_flow >= 0 ? 'text-blue-400' : 'text-orange-400'} font-bold text-lg`}>${financialReport.summary.net_flow.toFixed(2)}</p>
                  </div>
                </div>

                {/* Download Button */}
                <button
                  onClick={downloadReportCSV}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 py-3 rounded-full font-bold mb-4"
                >
                  📥 Download CSV Report
                </button>

                {/* Deposits List */}
                <div className="mb-4">
                  <h4 className="font-bold text-green-400 mb-2">✓ Deposits ({financialReport.deposits.length})</h4>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {financialReport.deposits.map((dep, idx) => (
                      <div key={idx} className="bg-green-500/10 border border-green-500/20 rounded-lg p-2 text-xs">
                        <div className="flex justify-between">
                          <span className="font-bold text-green-400">${dep.amount.toFixed(2)}</span>
                          <span className="text-muted-foreground">{dep.date}</span>
                        </div>
                        <p className="text-white">{dep.user_name} • {dep.phone}</p>
                      </div>
                    ))}
                    {financialReport.deposits.length === 0 && <p className="text-muted-foreground text-center py-2">No deposits</p>}
                  </div>
                </div>

                {/* Withdrawals List */}
                <div>
                  <h4 className="font-bold text-red-400 mb-2">↑ Withdrawals ({financialReport.withdrawals.length})</h4>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {financialReport.withdrawals.map((wd, idx) => (
                      <div key={idx} className="bg-red-500/10 border border-red-500/20 rounded-lg p-2 text-xs">
                        <div className="flex justify-between">
                          <span className="font-bold text-red-400">${wd.amount.toFixed(2)}</span>
                          <span className="text-muted-foreground">{wd.date}</span>
                        </div>
                        <p className="text-white">{wd.user_name} • {wd.phone}</p>
                      </div>
                    ))}
                    {financialReport.withdrawals.length === 0 && <p className="text-muted-foreground text-center py-2">No withdrawals</p>}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Games Admin Tab */}
      {tab === "games" && (
        <div className="space-y-4" data-testid="games-admin-tab">
          {loadingTab === "games" && <p className="text-center text-muted-foreground">Loading...</p>}

          {/* Stats Cards */}
          {gameStats && (
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 border border-green-500/30 rounded-xl p-3">
                <p className="text-xs text-green-300">Total Commission</p>
                <p className="text-xl font-bold text-green-400" data-testid="games-commission">
                  ${(gameStats.total_commission || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-card border border-border rounded-xl p-3">
                <p className="text-xs text-muted-foreground">Finished Games</p>
                <p className="text-xl font-bold" data-testid="games-total-played">
                  {gameStats.total_games_played || 0}
                </p>
              </div>
              <div className="bg-card border border-border rounded-xl p-3">
                <p className="text-xs text-muted-foreground">Active Rooms</p>
                <p className="text-xl font-bold text-yellow-400" data-testid="games-active-rooms">
                  {gameStats.active_rooms || 0}
                </p>
              </div>
            </div>
          )}

          {/* Pending Game Deposits */}
          <div className="bg-card border border-border rounded-2xl p-4">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Gamepad2 size={18} className="text-primary" /> Pending Game Deposits
            </h3>
            {pendingGameDeposits.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No pending deposits</p>
            ) : (
              <div className="space-y-2">
                {pendingGameDeposits.map((dep) => (
                  <div key={dep.id} className="bg-black/30 rounded-xl p-3" data-testid={`pending-dep-${dep.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold">${dep.amount?.toFixed(2)} USDT</p>
                        <p className="text-xs text-muted-foreground">{dep.user_name} • {dep.user_phone}</p>
                        <p className="text-xs text-muted-foreground">{new Date(dep.created_at).toLocaleString()}</p>
                        {dep.transaction_hash && (
                          <a
                            href={`https://bscscan.com/tx/${dep.transaction_hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-400 font-mono break-all mt-1 block hover:underline"
                            data-testid={`tx-hash-${dep.id}`}
                          >
                            {dep.transaction_hash}
                          </a>
                        )}
                      </div>
                      <div className="flex gap-2 ml-2">
                        <button
                          onClick={() => approveGameDeposit(dep.id)}
                          className="bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1"
                          data-testid={`approve-dep-${dep.id}`}
                        >
                          <CheckCircle2 size={14} /> Approve
                        </button>
                        <button
                          onClick={() => rejectGameDeposit(dep.id)}
                          className="bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1"
                          data-testid={`reject-dep-${dep.id}`}
                        >
                          <X size={14} /> Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending Game Withdrawals */}
          <div className="bg-card border border-border rounded-2xl p-4">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Gamepad2 size={18} className="text-orange-400" /> Pending Game Withdrawals
            </h3>
            {pendingGameWithdrawals.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No pending withdrawals</p>
            ) : (
              <div className="space-y-2">
                {pendingGameWithdrawals.map((wd) => (
                  <div key={wd.id} className="bg-black/30 rounded-xl p-3" data-testid={`pending-wd-${wd.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-orange-400">${wd.amount?.toFixed(2)} USDT</p>
                        <p className="text-xs text-muted-foreground">{wd.user_name} • {wd.user_phone}</p>
                        <p className="text-xs text-muted-foreground">{new Date(wd.created_at).toLocaleString()}</p>
                        <p className="text-xs text-blue-400 font-mono break-all mt-1">{wd.wallet_address}</p>
                      </div>
                      <div className="flex flex-col gap-1 ml-2">
                        <button
                          onClick={() => approveGameWithdrawal(wd.id)}
                          className="bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1"
                          data-testid={`approve-wd-${wd.id}`}
                        >
                          <CheckCircle2 size={14} /> Approve
                        </button>
                        <button
                          onClick={() => rejectGameWithdrawal(wd.id)}
                          className="bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1"
                          data-testid={`reject-wd-${wd.id}`}
                        >
                          <X size={14} /> Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* IP Location Modal */}
      {ipLocationModal && (
        <>
          <div className="modal-overlay" onClick={() => setIpLocationModal(null)}></div>
          <div className="modal-content">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-manrope font-bold text-xl">📍 IP Location Details</h2>
              <button onClick={() => setIpLocationModal(null)}><X size={24} /></button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-900/30 p-4 rounded-xl border border-blue-500/30">
                <p className="text-xs text-muted-foreground mb-1">IP Address</p>
                <p className="font-mono text-yellow-400 text-lg">{ipLocationModal.ip}</p>
              </div>
              
              {ipLocationModal.error ? (
                <p className="text-red-400">{ipLocationModal.error}</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-card p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">Country</p>
                      <p className="text-white font-bold">{ipLocationModal.country}</p>
                    </div>
                    <div className="bg-card p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">City</p>
                      <p className="text-white font-bold">{ipLocationModal.city}</p>
                    </div>
                    <div className="bg-card p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">Region</p>
                      <p className="text-white">{ipLocationModal.region}</p>
                    </div>
                    <div className="bg-card p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">Timezone</p>
                      <p className="text-white">{ipLocationModal.timezone}</p>
                    </div>
                  </div>
                  
                  <div className="bg-green-900/20 p-4 rounded-xl border border-green-500/30">
                    <p className="text-xs text-muted-foreground mb-2">Coordinates</p>
                    <p className="text-green-400 font-mono text-lg">
                      {ipLocationModal.latitude}, {ipLocationModal.longitude}
                    </p>
                    <a 
                      href={ipLocationModal.google_maps_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-2 inline-block px-4 py-2 bg-blue-600 rounded-lg text-sm font-bold"
                    >
                      🗺️ Open in Google Maps
                    </a>
                  </div>
                  
                  <div className="bg-card p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">ISP / Organization</p>
                    <p className="text-white">{ipLocationModal.isp}</p>
                    <p className="text-muted-foreground text-sm">{ipLocationModal.org}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* P2P Admin Tab */}
      {tab === "p2p" && (
        <div data-testid="p2p-admin-tab">
          <P2PAdminPanel />
        </div>
      )}

      {/* Analytics Tab */}
      {tab === "analytics" && (
        <div className="space-y-4" data-testid="admin-analytics-tab">
          {loadingTab === "analytics" && !analytics && (
            <div className="text-center py-8 text-muted-foreground">Loading analytics...</div>
          )}

          {analytics && (
            <>
              {/* Headline KPI cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/10 border border-green-500/30 rounded-xl p-4" data-testid="kpi-today-logins">
                  <div className="flex items-center gap-2 text-green-400 mb-1">
                    <UserCheck size={16} />
                    <span className="text-xs font-medium">Today's Logins</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{analytics.logins.unique_today}</p>
                  <p className="text-xs text-muted-foreground mt-1">Yesterday: {analytics.logins.unique_yesterday}</p>
                </div>

                <div className="bg-gradient-to-br from-red-600/20 to-orange-600/10 border border-red-500/30 rounded-xl p-4" data-testid="kpi-one-time-users">
                  <div className="flex items-center gap-2 text-red-400 mb-1">
                    <UserX size={16} />
                    <span className="text-xs font-medium">One-Time Users</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{analytics.one_time_only.count.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground mt-1">{analytics.one_time_only.percentage}% of total</p>
                </div>

                <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/10 border border-purple-500/30 rounded-xl p-4" data-testid="kpi-active-7d">
                  <div className="flex items-center gap-2 text-purple-400 mb-1">
                    <Activity size={16} />
                    <span className="text-xs font-medium">Active (7d)</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{analytics.logins.active_last_7d.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground mt-1">30d: {analytics.logins.active_last_30d.toLocaleString()}</p>
                </div>

                <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/10 border border-blue-500/30 rounded-xl p-4" data-testid="kpi-total-users">
                  <div className="flex items-center gap-2 text-blue-400 mb-1">
                    <Users size={16} />
                    <span className="text-xs font-medium">Total Users</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{analytics.totals.total_users.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground mt-1">New today: +{analytics.totals.new_today}</p>
                </div>
              </div>

              {/* Daily Trend Mini Chart */}
              <div className="bg-card rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp size={16} className="text-primary" />
                  <h3 className="text-sm font-bold text-white">14-Day Login Trend</h3>
                </div>
                {analytics.daily_trend && analytics.daily_trend.length > 0 ? (
                  <div className="flex items-end gap-1 h-32" data-testid="daily-trend-chart">
                    {(() => {
                      const maxUsers = Math.max(...analytics.daily_trend.map(d => d.unique_users), 1);
                      return analytics.daily_trend.map((d, i) => {
                        const h = Math.max(4, (d.unique_users / maxUsers) * 100);
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                            <div className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition">{d.unique_users}</div>
                            <div
                              className="w-full bg-gradient-to-t from-purple-600 to-pink-500 rounded-t hover:from-pink-500 hover:to-purple-400 transition"
                              style={{ height: `${h}%` }}
                              title={`${d.date}: ${d.unique_users} users, ${d.logins} logins`}
                            />
                            <div className="text-[9px] text-muted-foreground">{d.date.slice(5)}</div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No login data in last 14 days</p>
                )}
              </div>

              {/* One-time users by country */}
              {Object.keys(analytics.one_time_only.by_country || {}).length > 0 && (
                <div className="bg-card rounded-xl p-4">
                  <h3 className="text-sm font-bold text-white mb-3">One-Time Users by Country (Re-engagement Targets)</h3>
                  <div className="flex flex-wrap gap-2" data-testid="one-time-by-country">
                    {Object.entries(analytics.one_time_only.by_country)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 12)
                      .map(([country, cnt]) => (
                        <span key={country} className="px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-full text-xs text-red-300">
                          {country}: <span className="font-bold text-white">{cnt}</span>
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {/* Sub-view tabs */}
              <div className="flex gap-2 bg-card rounded-xl p-1">
                {[
                  { key: "overview", label: "📊 Overview" },
                  { key: "today", label: `✅ Today (${todayLoginsList.length})` },
                  { key: "onetime", label: `⚠️ One-Time (${oneTimeUsersList.length})` },
                ].map(v => (
                  <button
                    key={v.key}
                    onClick={() => setAnalyticsView(v.key)}
                    data-testid={`analytics-view-${v.key}`}
                    className={`flex-1 py-2 rounded-lg text-xs font-medium transition ${
                      analyticsView === v.key
                        ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                        : "text-muted-foreground hover:text-white"
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>

              {/* Today's Logins List */}
              {analyticsView === "today" && (
                <div className="bg-card rounded-xl p-3 max-h-[500px] overflow-y-auto" data-testid="today-logins-list">
                  {todayLoginsList.length === 0 ? (
                    <p className="text-center text-muted-foreground py-6 text-sm">No logins today yet</p>
                  ) : (
                    <div className="space-y-2">
                      {todayLoginsList.map((u) => (
                        <div key={u.user_id} className="flex items-center justify-between bg-black/30 p-2.5 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{u.full_name}</p>
                            <p className="text-xs text-muted-foreground">{u.phone} · {u.country || 'unknown'}</p>
                          </div>
                          <div className="text-right ml-2">
                            <p className="text-xs text-green-400">{u.login_count_today}x today</p>
                            <p className="text-[10px] text-muted-foreground">{new Date(u.last_login).toLocaleTimeString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* One-time Users List */}
              {analyticsView === "onetime" && (
                <div className="bg-card rounded-xl p-3 max-h-[500px] overflow-y-auto" data-testid="one-time-users-list">
                  <p className="text-xs text-yellow-300 mb-3">
                    Users who logged in only once. Top re-engagement candidates — send a personal nudge or promo.
                  </p>
                  {oneTimeUsersList.length === 0 ? (
                    <p className="text-center text-muted-foreground py-6 text-sm">No one-time users</p>
                  ) : (
                    <div className="space-y-2">
                      {oneTimeUsersList.map((u) => (
                        <div key={u.user_id} className="flex items-center justify-between bg-black/30 p-2.5 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{u.full_name || '—'}</p>
                            <p className="text-xs text-muted-foreground">{u.phone} · {u.country || 'unknown'}</p>
                          </div>
                          <div className="text-right ml-2">
                            <p className="text-[10px] text-muted-foreground">Last login</p>
                            <p className="text-xs text-orange-300">{new Date(u.last_login).toLocaleDateString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {analyticsView === "overview" && (
                <div className="bg-card rounded-xl p-4">
                  <h3 className="text-sm font-bold text-white mb-3">Quick Insights</h3>
                  <ul className="text-xs text-muted-foreground space-y-2 list-disc list-inside">
                    <li>Daily Active Rate: <span className="text-white font-medium">{((analytics.logins.unique_today / Math.max(analytics.totals.total_users, 1)) * 100).toFixed(2)}%</span> of all users logged in today.</li>
                    <li>Retention Loss: <span className="text-red-400 font-medium">{analytics.one_time_only.percentage}%</span> of users came once and never returned.</li>
                    <li>Weekly Active: <span className="text-white font-medium">{analytics.logins.active_last_7d.toLocaleString()}</span> users active in last 7 days.</li>
                    <li>Growth: <span className="text-green-400 font-medium">+{analytics.totals.new_last_7d}</span> new signups in last 7 days.</li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPage;
